/**
 * ReStaff Help Center — Article Content
 * All 8 module articles with full structured content.
 */

export const ARTICLES = {

  /* ─────────────────────────────────────────────
     TIME CLOCK
  ───────────────────────────────────────────── */
  'time-clock': {
    title: 'Time Clock',
    description: 'Track employee attendance in real time using PIN-based kiosk clock-in and clock-out.',
    icon: '⏱',
    category: 'Core Features',
    demo: 'clock-in',
    screenshot: '/help/timeclock.jpg',
    sections: [
      { id: 'overview',         label: 'Overview' },
      { id: 'benefits',         label: 'Key Benefits' },
      { id: 'when-to-use',      label: 'When to Use' },
      { id: 'setup',            label: 'Setup' },
      { id: 'guide',            label: 'Step-by-Step' },
      { id: 'tips',             label: 'Tips' },
      { id: 'troubleshooting',  label: 'Troubleshooting' },
      { id: 'faq',              label: 'FAQ' },
    ],
    overview: `The Time Clock module lets employees clock in and out using a secure 4-digit PIN on any shared kiosk device, tablet, or dedicated terminal. All clock events are automatically recorded and populate employee timesheets — removing manual time tracking entirely.`,
    benefits: [
      'Accurate, tamper-proof payroll hours',
      'Eliminates manual timesheets and guesswork',
      'Real-time attendance visibility for managers',
      'Supports multi-location deployments from a single dashboard',
      'Prevents early clock-ins with shift-lock enforcement',
    ],
    whenToUse: `Use the Time Clock whenever employees begin or end a shift. Place kiosk devices at location entrances so staff can clock in the moment they arrive. Managers use the Timesheets module to review, approve, and edit clock events.`,
    onboarding: `First time setting up the Time Clock? Start here: assign a unique PIN to each employee in Staff Management, then open the Kiosk on a shared tablet at your location entrance.`,
    steps: [
      {
        title: 'Open the Kiosk',
        desc: 'Navigate to Time Clock in the sidebar or visit /time-clock directly. The kiosk view is optimised for tablets and touch screens. You can lock the browser into this view to prevent staff from navigating elsewhere.',
        tip: 'Use a dedicated tablet mounted near the entrance for the best employee experience.',
      },
      {
        title: 'Employee Enters PIN',
        desc: 'Each employee presses their assigned 4-digit PIN on the on-screen keypad. PINs are encrypted and location-specific — the same employee can have different PINs at different locations.',
        tip: 'PINs are set in Staff Management → Edit Employee → Set PIN.',
      },
      {
        title: 'Clock In or Clock Out',
        desc: 'After PIN validation, the employee sees their name and shift status. They tap Clock In to start a shift or Clock Out to end one. The exact timestamp is recorded immediately.',
        tip: 'The system shows current shift time so employees can verify they are on track.',
      },
      {
        title: 'Manager Reviews Entries',
        desc: 'In the Timesheets module, managers can view all clock events, see total hours per employee, approve or reject entries, and edit timestamps if a correction is needed.',
        tip: 'Bulk-approve all entries for a pay period with one click using the Bulk Approve feature.',
      },
    ],
    tips: [
      'Mount the tablet near the entrance so clocking in is part of the natural arrival flow.',
      'Set a screen lock on the kiosk browser to prevent staff from accessing other apps.',
      'Review the Timesheets module daily rather than at the end of the pay period to catch errors early.',
      'If an employee forgets their PIN, reset it instantly from Staff Management — no downtime.',
      'Use the All Locations view in Timesheets to monitor attendance across every branch simultaneously.',
    ],
    troubleshooting: [
      {
        problem: 'Employee forgot to clock out',
        solution: 'Go to Timesheets → find the open entry → click Edit → set the correct end time manually. The corrected entry will be reflected in payroll hours immediately.',
      },
      {
        problem: 'Employee entered the wrong PIN',
        solution: 'After 5 failed attempts the kiosk shows a cooldown message. Wait 60 seconds and try again. If the PIN is truly unknown, reset it in Staff Management → Edit Employee → Reset PIN.',
      },
      {
        problem: 'Kiosk is not showing the correct location',
        solution: 'The kiosk URL contains a location-specific token. Ensure you opened the kiosk from the correct location in Time Clock → select the right location from the dropdown at the top.',
      },
      {
        problem: 'Clock-in time shows wrong timezone',
        solution: 'ReStaff uses the server time. Ensure your business location settings reflect the correct timezone in Settings → Locations → Edit.',
      },
    ],
    faq: [
      { q: 'Can employees clock in from their personal phone?', a: 'Yes. Employees can access the kiosk page from any browser. The manager can share the kiosk link with staff for remote or field-based clock-in.' },
      { q: 'Can two employees share a PIN?', a: 'No. PINs are unique per employee per location. The system will reject duplicate PINs during setup.' },
      { q: 'Does the Time Clock work without internet?', a: 'No. A live internet connection is required to record clock events to the server in real time.' },
      { q: 'Can managers clock in on behalf of an employee?', a: 'Managers can manually add or edit timesheet entries from the Timesheets module if an employee missed their clock-in.' },
      { q: 'How far back can I edit a time entry?', a: 'There is no hard limit. Managers with the correct role can edit any historical entry at any time.' },
    ],
  },

  /* ─────────────────────────────────────────────
     SCHEDULE
  ───────────────────────────────────────────── */
  'schedule': {
    title: 'Schedule',
    description: 'Build and publish weekly staff schedules with drag-and-drop shift assignment.',
    icon: '📅',
    category: 'Core Features',
    demo: 'schedule',
    screenshot: '/help/schedule.jpg',
    sections: [
      { id: 'overview',        label: 'Overview' },
      { id: 'benefits',        label: 'Key Benefits' },
      { id: 'when-to-use',     label: 'When to Use' },
      { id: 'guide',           label: 'Step-by-Step' },
      { id: 'tips',            label: 'Tips' },
      { id: 'troubleshooting', label: 'Troubleshooting' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `The Schedule module is a visual weekly planner for building, editing, and publishing staff schedules. Managers create shifts, assign employees, and the schedule is instantly visible to all staff in their Employee Portal. Employees can also view their upcoming shifts from any device.`,
    benefits: [
      'Visual drag-and-drop week view for fast scheduling',
      'Direct employee assignment — drop shifts on staff rows',
      'Copy Last Week to duplicate a working schedule in one click',
      'Bulk Shift Creator for adding multiple shifts at once',
      'Publish / Unpublish schedules — employees only see published versions',
      'Draft vs. Published states prevent incomplete schedules from going live',
      'Role-based shift templates for repeatable patterns',
      'Staff see their schedule in real time on the Employee Portal',
      'Conflict detection prevents double-booking',
      'Filter by employee, role, or location',
    ],
    whenToUse: `Use the Schedule module at the start of each week (or further in advance) to plan shifts for all staff. Publish the schedule once finalized so employees can plan around their upcoming shifts.`,
    onboarding: `New to scheduling? Start by adding all your staff in Staff Management, then come back to Schedule to assign their first week of shifts.`,
    steps: [
      {
        title: 'Open the Schedule',
        desc: 'Click Schedule in the sidebar. The default view shows the current week with all active employees in rows and days in columns. Use the arrows to navigate forward or backward in time.',
        tip: 'Use the location filter at the top to view schedules for a specific branch.',
      },
      {
        title: 'Create a New Shift',
        desc: 'Click any empty cell in the schedule grid to open the Add Shift dialog. Set the start time, end time, and role/position. Confirm to save the shift.',
        tip: 'You can create shifts days or weeks in advance by navigating forward in the calendar.',
      },
      {
        title: 'Assign an Employee',
        desc: 'In the Add Shift dialog, select the employee from the dropdown. Only active employees assigned to this location will appear in the list.',
        tip: 'If an employee is not appearing, check that they are assigned to the current location in Staff Management → Staff → Locations.',
      },
      {
        title: 'Edit or Delete a Shift',
        desc: 'Click an existing shift card to open its detail panel. You can change the time, reassign the shift to another employee, or delete it.',
        tip: 'Deleting a shift removes it from the employee view immediately.',
      },
      {
        title: 'Staff Views Their Schedule',
        desc: 'Once shifts are saved, employees can see their upcoming schedule in the Employee Portal under My Schedule. Schedules are updated in real time.',
        tip: 'Encourage staff to check their schedule on Sunday evening to plan their week.',
      },
    ],
    tips: [
      'Schedule at least 5 days in advance so employees can plan around their personal commitments.',
      'Use copy-week features (if available) to duplicate a working schedule pattern for future weeks.',
      'Always check the availability tab before assigning a shift to an employee who submitted unavailability.',
      'Brief morning and evening shifts often benefit from different staffing — use the role filter to review coverage by position.',
      'After scheduling, cross-reference with the Timesheets module at the end of the week to see actual vs. planned hours.',
    ],
    troubleshooting: [
      {
        problem: 'An employee is not showing in the assignee dropdown',
        solution: 'Ensure the employee is marked as Active and assigned to the correct location. Go to Staff Management → find the employee → check their Location Assignments.',
      },
      {
        problem: 'Shift appears on wrong date',
        solution: 'Check that the correct week is displayed. Use the week navigation arrows to confirm you are viewing the intended week.',
      },
      {
        problem: 'Employee says they cannot see their schedule',
        solution: 'Ask the employee to log into their Employee Portal at /employee/schedule. If the schedule is still blank, ensure the shift is saved and assigned to their account (not just to a role/position).',
      },
    ],
    faq: [
      { q: 'Can employees request schedule changes?', a: 'Employees can submit availability preferences from the Employee Portal. Managers review these before finalizing the schedule.' },
      { q: 'Can I schedule the same employee for two shifts in one day?', a: 'Yes. Multiple shifts per day per employee are supported. Each appears as a separate card in the schedule grid.' },
      { q: 'Is there a mobile-friendly schedule view for managers?', a: 'Yes. The schedule is fully responsive. Managers can view and edit schedules from any mobile browser.' },
      { q: 'Can I see all locations in one schedule view?', a: 'Use the All Locations filter at the top of the schedule to see shifts across every branch simultaneously.' },
    ],
  },

  /* ─────────────────────────────────────────────
     SHIFT SWAPS
  ───────────────────────────────────────────── */
  'shift-swaps': {
    title: 'Shift Swaps',
    description: 'Let employees request shift exchanges and get manager approval — all in one place.',
    icon: '🔁',
    category: 'Workforce Management',
    demo: 'shift-swap',
    sections: [
      { id: 'overview',        label: 'Overview' },
      { id: 'benefits',        label: 'Key Benefits' },
      { id: 'when-to-use',     label: 'When to Use' },
      { id: 'guide',           label: 'Step-by-Step' },
      { id: 'tips',            label: 'Tips' },
      { id: 'troubleshooting', label: 'Troubleshooting' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `The Shift Swaps module provides a structured workflow for employees to propose shift exchanges with coworkers, with manager approval required before any change takes effect. This eliminates informal WhatsApp-style arrangements and keeps your schedule accurate.`,
    benefits: [
      'Employees resolve coverage gaps themselves — less manager involvement',
      'All swap requests are logged with a full audit trail',
      'Manager retains final approval — no unauthorized swaps',
      'Reduces no-shows caused by forgotten informal arrangements',
      'Both employees are notified when a swap is approved or denied',
    ],
    whenToUse: `Use Shift Swaps when an employee cannot work their assigned shift and has found a willing coworker to cover. Both parties submit the swap request, and the manager approves or denies it.`,
    onboarding: `First time using Shift Swaps? Ensure all staff have active accounts and are assigned to the same location. Swaps are only possible between employees at the same location.`,
    steps: [
      {
        title: 'Employee Requests a Swap',
        desc: 'The requesting employee goes to Shift Swaps in the navigation and clicks New Swap Request. They select their shift and the coworker they want to swap with, then submit.',
        tip: 'Employees can only request swaps for shifts they are personally scheduled for.',
      },
      {
        title: 'Coworker Confirms',
        desc: 'The receiving employee receives a notification. They review the request in their Shift Swaps page and accept or decline.',
        tip: 'If the coworker declines, the requesting employee can approach a different coworker.',
      },
      {
        title: 'Manager Reviews',
        desc: 'Once both employees have agreed, the swap appears in the manager\'s Shift Swaps view with a Pending Approval status. The manager clicks Approve or Deny.',
        tip: 'Always check for coverage conflicts before approving — ensure the receiving employee is not already scheduled at the same time.',
      },
      {
        title: 'Schedule Updated',
        desc: 'After manager approval, the schedule is automatically updated to reflect the new assignment. Both employees receive a confirmation notification.',
        tip: 'If a swap is approved by mistake, managers can revert it by editing the original shifts in the Schedule module.',
      },
    ],
    tips: [
      'Encourage employees to submit swap requests at least 48 hours before the shift.',
      'Review all pending swaps at the start of each day to avoid last-minute surprises.',
      'If your operation has role requirements, check that the covering employee has the correct role before approving.',
      'Use the Denied status to communicate back to employees why a swap was rejected.',
    ],
    troubleshooting: [
      {
        problem: 'Swap request is stuck in Pending',
        solution: 'Check if the coworker has accepted their side of the request. The swap only moves to manager review after both parties agree. Follow up with the second employee directly.',
      },
      {
        problem: 'Manager cannot see the swap request',
        solution: 'Ensure the manager is viewing the correct location. Use the location filter in Shift Swaps to switch between branches.',
      },
      {
        problem: 'Schedule did not update after approval',
        solution: 'Refresh the Schedule page. If the change is still not showing, open the shift in the Schedule module and manually reassign it to match the approved swap.',
      },
    ],
    faq: [
      { q: 'Can an employee swap with someone from a different location?', a: 'Not by default. Swaps are location-specific. If your operation allows cross-location coverage, assign the employee to both locations first.' },
      { q: 'What happens if the manager denies the swap?', a: 'Both employees are notified. The original schedule remains unchanged. The requesting employee must find another solution or work their original shift.' },
      { q: 'Can a manager initiate a swap on behalf of employees?', a: 'Managers can directly edit assignments in the Schedule module instead of using the swap workflow.' },
      { q: 'Is there a limit to how many swaps an employee can request?', a: 'No hard limit is enforced in the current version.' },
    ],
  },

  /* ─────────────────────────────────────────────
     TIMESHEETS
  ───────────────────────────────────────────── */
  'timesheets': {
    title: 'Timesheets',
    description: 'Review, approve, edit, and export employee worked hours for payroll.',
    icon: '📋',
    category: 'Workforce Management',
    demo: 'timesheet',
    screenshot: '/help/timesheets.jpg',
    sections: [
      { id: 'overview',        label: 'Overview' },
      { id: 'benefits',        label: 'Key Benefits' },
      { id: 'when-to-use',     label: 'When to Use' },
      { id: 'guide',           label: 'Step-by-Step' },
      { id: 'tips',            label: 'Tips' },
      { id: 'troubleshooting', label: 'Troubleshooting' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `The Timesheets module aggregates all clock-in and clock-out events into a structured time log. Managers review entries, approve or reject them individually (with reasons), and export final records to CSV or Excel for payroll processing.`,
    benefits: [
      'One-click bulk approval for clean pay periods',
      'Rejection reasons keep staff informed of corrections',
      'CSV and Excel export for any payroll provider',
      'Full audit trail: who approved what, and when',
      'Payroll summary grouped by employee for fast review',
    ],
    whenToUse: `Review timesheets daily to catch missing clock-outs or errors early. Run a final approval pass at the end of each pay period before exporting to payroll.`,
    onboarding: `Starting fresh? Your first timesheets will appear automatically once employees begin clocking in via the Time Clock. No manual setup is required.`,
    steps: [
      {
        title: 'Open Timesheets',
        desc: 'Click Timesheets in the sidebar. You will see a list of all timesheet entries filtered to the current week and your selected location.',
        tip: 'Use the date range picker to narrow down entries to a specific pay period.',
      },
      {
        title: 'Review Entries',
        desc: 'Each row shows: employee name, clock-in time, clock-out time, total hours, and current status (Pending, Approved, Rejected). Click a row to expand its detail.',
        tip: 'Sort by status to batch-process all Pending entries at once.',
      },
      {
        title: 'Approve or Reject',
        desc: 'Click Approve to mark an entry as correct. Click Reject to open a reason modal — type a clear reason so the employee understands what happened. Both actions are logged with your name and timestamp.',
        tip: 'Employees can see the rejection reason by hovering the Rejected badge in their own Timesheets view.',
      },
      {
        title: 'Edit an Entry',
        desc: 'If the clock-in or clock-out time is wrong, click Edit on any entry and correct the timestamps. The edit is saved with an audit record showing who changed what.',
        tip: 'Only edit when you have a clear reason — the original timestamp is preserved in the audit log.',
      },
      {
        title: 'Bulk Approve',
        desc: 'Use the checkboxes to select multiple entries and click Bulk Approve to approve all at once. This is ideal at the end of a pay period when entries have already been reviewed.',
        tip: 'Filter to Pending status first so you only see entries that still need action.',
      },
      {
        title: 'Export to Payroll',
        desc: 'Click Export → CSV or Export → Excel to download the timesheet data. The export includes all visible entries with hours, names, and timestamps. Import this file into your payroll software.',
        tip: 'Use the Payroll Summary export for a per-employee hour total rather than individual entries.',
      },
    ],
    tips: [
      'Run a daily 5-minute review of new timesheet entries — catching errors early prevents end-of-period scrambling.',
      'Use bulk approval for large teams: filter by date and Pending status, select all, approve in one action.',
      'Always include a clear rejection reason so employees can self-correct without needing to call the manager.',
      'The Payroll Summary export is pre-formatted for most payroll platforms — use this over the raw CSV.',
      'Archive completed pay periods by exporting before the period closes so you have historical records offline.',
    ],
    troubleshooting: [
      {
        problem: 'Entry shows 0 hours',
        solution: 'The employee clocked in but never clocked out. Click Edit on the entry and set the correct clock-out time. This is the most common timesheet issue.',
      },
      {
        problem: 'I approved an entry by mistake',
        solution: 'Currently there is no un-approve button. Edit the entry to add a note, then contact payroll manually to flag the correction. Contact support if bulk-correction is needed.',
      },
      {
        problem: 'Export file is empty',
        solution: 'Check that the date range filter includes the pay period you want to export. If no entries are visible on screen, the export will also be empty.',
      },
      {
        problem: 'Rejected entry still shows in employee view',
        solution: 'Rejection is visible in the employee\'s Timesheets view with the reason shown as a tooltip on the Rejected badge. This is expected — employees can see their rejected entries for awareness.',
      },
    ],
    faq: [
      { q: 'Can employees edit their own timesheets?', a: 'No. Only managers and owners can edit timesheet entries. Employees can view their entries and see rejection reasons, but cannot modify timestamps.' },
      { q: 'What export formats are supported?', a: 'CSV and Excel (.xlsx). Both contain the same data in slightly different formats. Use Excel for human review and CSV for importing into payroll software.' },
      { q: 'How long are timesheet records kept?', a: 'Records are stored indefinitely in ReStaff. There is no automatic deletion.' },
      { q: 'Can I see timesheets for all locations at once?', a: 'Yes. Use the All Locations option in the location filter at the top of the page to see entries from every branch.' },
      { q: 'Why does an entry show a negative total?', a: 'This happens when the clock-out time recorded is earlier than the clock-in time — usually a data entry error. Edit the entry to correct the timestamps.' },
    ],
  },

  /* ─────────────────────────────────────────────
     REPORTS
  ───────────────────────────────────────────── */
  'reports': {
    title: 'Reports',
    description: 'Analyze labor hours, staffing trends, and operational data with exportable reports.',
    icon: '📊',
    category: 'Analytics',
    demo: 'report',
    screenshot: '/help/reports.jpg',
    sections: [
      { id: 'overview',        label: 'Overview' },
      { id: 'benefits',        label: 'Key Benefits' },
      { id: 'when-to-use',     label: 'When to Use' },
      { id: 'guide',           label: 'Step-by-Step' },
      { id: 'tips',            label: 'Tips' },
      { id: 'troubleshooting', label: 'Troubleshooting' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `The Reports module provides pre-built labor analytics reports. View hours worked by employee or location, weekly labor summaries, and export data for accounting or compliance purposes. Reports pull from approved timesheet entries in real time.`,
    benefits: [
      'Pre-built reports — no setup required',
      'Filter by location, employee, date range, or role',
      'Export to CSV for external analysis',
      'Weekly summary view for fast period-end review',
      'Data updates in real time as timesheets are approved',
    ],
    whenToUse: `Run reports at the end of each week to review labor totals, at the end of each pay period before processing payroll, and quarterly for trend analysis and budget planning.`,
    onboarding: `Reports populate automatically from approved timesheet data. The more consistently staff use the Time Clock, the more complete your reports will be.`,
    steps: [
      {
        title: 'Open Reports',
        desc: 'Click Reports in the sidebar. The default view shows the Hours Report for the current week.',
        tip: 'Switch between report types using the tabs at the top of the page.',
      },
      {
        title: 'Set Date Range and Filters',
        desc: 'Use the date range picker to select the period you want to analyze. Apply optional filters for location or specific employees to narrow down the data.',
        tip: 'For pay period reporting, match the date range to your exact payroll cycle start and end dates.',
      },
      {
        title: 'Review Hours Report',
        desc: 'The Hours Report shows total worked hours per employee, broken down by day. You can quickly spot who worked overtime or who has fewer hours than expected.',
        tip: 'Employees with significantly fewer hours than usual may have missed clock-outs — check their timesheets.',
      },
      {
        title: 'Review Weekly Summary',
        desc: 'The Weekly Summary aggregates total hours per location and per role. This is useful for labor cost planning and comparing staffing levels week over week.',
        tip: 'Compare weekly summaries month over month to identify staffing trends for seasonal planning.',
      },
      {
        title: 'Export the Report',
        desc: 'Click Export CSV to download the currently visible report data. The file includes all rows with employee names, dates, and hours.',
        tip: 'CSV files open in Excel, Google Sheets, or can be imported directly into accounting software.',
      },
    ],
    tips: [
      'Run the Weekly Summary every Friday to get a preview of labor costs before the pay period closes.',
      'Compare this week\'s hours to last week to spot staffing anomalies early.',
      'Save exported CSVs in a organized folder by pay period for clean accounting records.',
      'Cross-reference the Hours Report with the Schedule to identify gaps between planned and actual hours.',
      'For overtime management, filter the Hours Report by employee and look for any individual exceeding 40 hours in a week.',
    ],
    troubleshooting: [
      {
        problem: 'Report shows fewer hours than expected',
        solution: 'Hours in Reports only reflect approved timesheet entries. If entries are still in Pending status, they will not appear in the report. Go to Timesheets and approve the relevant entries first.',
      },
      {
        problem: 'Export file has no data',
        solution: 'Ensure the date range and location filters match the period you want to export. If the table on screen is empty, the export will also be empty.',
      },
      {
        problem: 'An employee\'s hours look incorrect',
        solution: 'Check their individual timesheet entries in the Timesheets module for the same period. Look for missing clock-outs (0 hours entries) or duplicate entries that would skew totals.',
      },
    ],
    faq: [
      { q: 'Do reports include unapproved timesheet entries?', a: 'No. Reports only include entries with an Approved status. Make sure to review and approve timesheets before running reports for payroll.' },
      { q: 'Can I see a report for just one employee?', a: 'Yes. Use the employee filter in the Hours Report to narrow down to a single staff member.' },
      { q: 'What is the difference between Hours Report and Weekly Summary?', a: 'Hours Report shows individual clock-in/out entries. Weekly Summary aggregates totals by employee or location for the selected week.' },
      { q: 'How far back can I pull report data?', a: 'There is no limit. You can select any historical date range to generate reports from all time.' },
    ],
  },

  /* ─────────────────────────────────────────────
     EXECUTIVE INSIGHTS
  ───────────────────────────────────────────── */
  'executive-insights': {
    title: 'Executive Insights',
    description: 'Monitor workforce efficiency, labor costs, and staffing health metrics at a glance.',
    icon: '📈',
    category: 'Analytics',
    demo: 'budget',
    screenshot: '/help/executive-insights.jpg',
    sections: [
      { id: 'overview',        label: 'Overview' },
      { id: 'benefits',        label: 'Key Benefits' },
      { id: 'when-to-use',     label: 'When to Use' },
      { id: 'guide',           label: 'Step-by-Step' },
      { id: 'tips',            label: 'Tips' },
      { id: 'troubleshooting', label: 'Troubleshooting' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `Executive Insights is the command-center dashboard for owners and senior managers. It surfaces key workforce metrics — labor health, budget vs. actual spend, attendance patterns, and risk alerts — in a single view across all your locations.`,
    benefits: [
      'Single-screen visibility across all business locations',
      'Budget vs. Actual labor cost tracking per location',
      'Staffing risk alerts for over-budget or underperforming periods',
      'Week-over-week trends for headcount and hours',
      'No configuration required — populates from live data',
    ],
    whenToUse: `Check Executive Insights every Monday morning for a week-ahead workforce view and every Friday for a week-end review. Use it during planning sessions when comparing locations or reviewing budget adherence.`,
    onboarding: `Executive Insights is available immediately after your first employees clock in. For Budget vs. Actual tracking, set a weekly labor budget for each location in Settings → Locations → Edit → Labor Budget.`,
    steps: [
      {
        title: 'Open Executive Insights',
        desc: 'Click Executive Insights in the sidebar. This page is only available to owners and managers.',
        tip: 'Use the location filter at the top to switch between viewing all locations or a single branch.',
      },
      {
        title: 'Review the Overview Cards',
        desc: 'The top row shows summary cards: total hours worked this week, number of active staff, estimated labor cost, and locations currently over budget. These update daily.',
        tip: 'Red or amber indicators on overview cards signal items that need immediate attention.',
      },
      {
        title: 'Analyze Labor Health',
        desc: 'The Labor Health section shows attendance rates, clock-in punctuality, and shift coverage percentages for each location. Green means healthy; amber and red mean action is needed.',
        tip: 'Low clock-in rates often signal scheduling gaps or Time Clock setup issues at a location.',
      },
      {
        title: 'Review Budget vs. Actual',
        desc: 'The Budget vs. Actual card compares your set weekly labor budget per location against the estimated actual spend. Progress bars turn red when a location exceeds its budget.',
        tip: 'Set labor budgets in Settings → Locations → Edit → Labor Budget section.',
      },
      {
        title: 'Act on Risk Alerts',
        desc: 'If any location shows a risk alert (over-budget, no shifts scheduled, or missing timesheets), click the alert to navigate directly to the relevant module for correction.',
        tip: 'Clearing alerts weekly keeps your operations running cleanly and prevents compounding issues.',
      },
    ],
    tips: [
      'Set realistic labor budgets in Settings before using Budget vs. Actual — without a budget configured, the comparison card shows no data.',
      'Monday morning reviews take 5 minutes — make it a weekly ritual for all managers.',
      'Use the multi-location view to quickly spot which branch needs the most attention this week.',
      'If a location consistently exceeds budget, investigate whether scheduling is being done efficiently.',
      'The estimated labor cost is based on an average hourly rate — actual costs may vary by individual wage. Use this as a directional indicator, not an accounting figure.',
    ],
    troubleshooting: [
      {
        problem: 'Budget vs. Actual shows no data',
        solution: 'A weekly labor budget must be set for each location. Go to Settings → Locations → Edit the location → scroll to the Labor Budget section and enter a weekly dollar amount.',
      },
      {
        problem: 'Labor health metrics look inaccurate',
        solution: 'Metrics are computed from approved timesheet entries. If timesheets have not been reviewed and approved this week, the numbers may be incomplete. Approve pending timesheets first.',
      },
      {
        problem: 'I can only see one location\'s data',
        solution: 'Use the location switcher at the top of the page. Select All Locations to see aggregated data across all branches, or switch between individual locations.',
      },
    ],
    faq: [
      { q: 'Who can access Executive Insights?', a: 'Owners and managers. Staff accounts do not have access to this page.' },
      { q: 'How is the estimated labor cost calculated?', a: 'ReStaff multiplies total worked hours by a default average hourly rate of $15. This is a planning estimate — set your own labor budget in Settings for more accurate comparisons.' },
      { q: 'Does the Budget vs. Actual card update in real time?', a: 'It updates daily based on approved timesheet entries. Real-time updates require timesheets to be approved as shifts are completed.' },
      { q: 'Can I export Executive Insights data?', a: 'Use the Reports module for exportable data. Executive Insights is a summary view only and does not currently have a direct export option.' },
    ],
  },

  /* ─────────────────────────────────────────────
     DOCUMENTS
  ───────────────────────────────────────────── */
  'documents': {
    title: 'Documents',
    description: 'Upload, manage, and share internal documents with your entire team.',
    icon: '📁',
    category: 'Administration',
    demo: 'document',
    sections: [
      { id: 'overview',        label: 'Overview' },
      { id: 'benefits',        label: 'Key Benefits' },
      { id: 'when-to-use',     label: 'When to Use' },
      { id: 'guide',           label: 'Step-by-Step' },
      { id: 'tips',            label: 'Tips' },
      { id: 'troubleshooting', label: 'Troubleshooting' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `The Documents module is a centralized file repository for your business. Upload handbooks, compliance forms, training materials, and internal policies. Staff can access shared documents from the Employee Portal — no email chains or shared drives required.`,
    benefits: [
      'Single source of truth for all company documents',
      'Staff access documents 24/7 from the Employee Portal',
      'Staff acknowledgment tracking — know who has read critical policies',
      'Reduces email overhead for distributing files',
      'Supports all common file types: PDF, Word, Excel, images',
      'Manager control over which documents are shared vs. private',
      'Centralized HR storage for compliance and onboarding materials',
    ],
    whenToUse: `Upload documents when onboarding new employees, updating company policies, sharing safety or compliance materials, or distributing any document that all staff should have access to.`,
    onboarding: `Start by uploading your Employee Handbook and any safety guidelines. These are the documents most frequently needed by new hires.`,
    steps: [
      {
        title: 'Open Documents',
        desc: 'Click Documents in the sidebar. You will see a list of all uploaded files with their names, sizes, upload dates, and sharing status.',
        tip: 'Sort by upload date to find recently added files quickly.',
      },
      {
        title: 'Upload a Document',
        desc: 'Click Upload Document. Select a file from your computer (PDF, DOCX, XLSX, PNG, JPG, etc.). The file is uploaded securely to cloud storage. Large files may take a few seconds.',
        tip: 'Keep file sizes under 25 MB for fast uploads. Compress large PDFs with a free tool like Smallpdf before uploading.',
      },
      {
        title: 'Share with Staff',
        desc: 'After uploading, toggle the Shared switch on a document to make it visible to all active employees in the Employee Portal under Documents.',
        tip: 'Shared documents are visible to all employees at the location — they cannot be targeted to specific individuals in the current version.',
      },
      {
        title: 'Download or View',
        desc: 'Click any document title to download it immediately. On mobile, most PDFs open directly in the browser viewer.',
        tip: 'Share the /employee/documents link with staff so they know where to find their documents.',
      },
      {
        title: 'Delete a Document',
        desc: 'Click the delete icon (trash) next to a document to permanently remove it. Deleted documents are removed from the employee view immediately.',
        tip: 'Before deleting, verify that no staff are actively referencing or filling out the document.',
      },
    ],
    tips: [
      'Name files clearly — use descriptive names like "Employee Handbook v2.pdf" instead of "handbook_final_FINAL.pdf".',
      'Keep a "Private" version of sensitive documents and only share what employees need.',
      'Review your document library quarterly and remove outdated policies to avoid confusion.',
      'Upload compliance documents as soon as they are updated to ensure staff always have the latest version.',
      'Announce new shared documents via the Announcements module so staff know to look for them.',
    ],
    troubleshooting: [
      {
        problem: 'Upload fails or hangs',
        solution: 'Check your internet connection and file size. Files over 25 MB may time out. Try compressing the file and uploading again. If the issue persists, try a different browser.',
      },
      {
        problem: 'Staff cannot see a document I uploaded',
        solution: 'Ensure the document\'s Shared toggle is turned ON. Private documents (toggle OFF) are only visible to managers and owners.',
      },
      {
        problem: 'File cannot be opened',
        solution: 'Download the file first instead of trying to view it in the browser. Some file types (DOCX, XLSX) need a desktop application like Microsoft Office or Google Drive to open.',
      },
    ],
    faq: [
      { q: 'What file types are supported?', a: 'PDF, DOCX, DOC, XLSX, XLS, PPT, PPTX, PNG, JPG, GIF, and most common document formats. Files are served directly from cloud storage.' },
      { q: 'Is there a storage limit?', a: 'Storage limits depend on your subscription plan. Check Settings → Billing for your current storage usage.' },
      { q: 'Can employees upload documents?', a: 'No. Only managers and owners can upload files. Employees have read-only access to shared documents.' },
      { q: 'Are documents backed up?', a: 'Yes. Files are stored on redundant cloud storage (Bunny.net CDN) and are accessible from any device.' },
      { q: 'Can I restrict a document to a specific location?', a: 'Currently, shared documents are visible to all active staff in your organization. Location-specific document access is on the product roadmap.' },
    ],
  },

  /* ─────────────────────────────────────────────
     STAFF MANAGEMENT
  ───────────────────────────────────────────── */
  'staff': {
    title: 'Staff Management',
    description: 'Add employees, configure roles, assign locations, and manage PIN access.',
    icon: '👥',
    category: 'Administration',
    demo: 'staff',
    screenshot: '/help/staff.jpg',
    sections: [
      { id: 'overview',        label: 'Overview' },
      { id: 'benefits',        label: 'Key Benefits' },
      { id: 'when-to-use',     label: 'When to Use' },
      { id: 'guide',           label: 'Step-by-Step' },
      { id: 'tips',            label: 'Tips' },
      { id: 'troubleshooting', label: 'Troubleshooting' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `Staff Management is the central hub for your workforce directory. Add new employees, assign them to locations, set their role permissions (Staff or Manager), configure their Time Clock PIN, and manage their active status — all from one page.`,
    benefits: [
      'Centralized employee directory across all locations',
      'Role-based permissions: Staff sees their own data, Manager manages a location',
      'Secure encrypted PIN per employee for kiosk access',
      'Multi-location assignment for employees who work at multiple branches',
      'Instant deactivation to remove access without deleting records',
    ],
    whenToUse: `Use Staff Management when onboarding a new hire, updating an employee\'s role, deactivating a departing employee, reassigning staff to a new location, or resetting a forgotten PIN.`,
    onboarding: `Before your team can clock in or see their schedule, they need accounts in ReStaff. Start here by adding each employee. If you have many staff, contact support for bulk-import options.`,
    steps: [
      {
        title: 'Open Staff Management',
        desc: 'Click Staff in the sidebar. You will see a directory of all employees at your selected location. Use the location filter to switch between branches.',
        tip: 'Use the search bar to find a specific employee by name in large organizations.',
      },
      {
        title: 'Add a New Employee',
        desc: 'Click Add Employee. Enter their full name, email address, and role (Staff or Manager). The system sends them a welcome email with login instructions and a temporary password.',
        tip: 'Staff accounts only access the Employee Portal. Manager accounts access the full dashboard for their location.',
      },
      {
        title: 'Assign to a Location',
        desc: 'After creation, click the employee\'s card and go to the Locations tab. Assign them to one or more locations. Their schedule and timesheets will be scoped to these locations.',
        tip: 'An employee must be assigned to a location before they can be added to a schedule or use the kiosk.',
      },
      {
        title: 'Set a Time Clock PIN',
        desc: 'Click Set PIN on the employee card to generate or manually enter a 4-digit PIN. This PIN is encrypted and used exclusively at the Time Clock kiosk.',
        tip: 'Use the auto-generate option for secure random PINs, then share the PIN privately with the employee.',
      },
      {
        title: 'Edit or Deactivate',
        desc: 'Click Edit on any employee to update their name, email, or role. To remove access without deleting records, toggle their status to Inactive. Inactive employees cannot log in or use the kiosk.',
        tip: 'Always deactivate departing employees on their last day to immediately revoke all access.',
      },
    ],
    tips: [
      'Add employees at least one day before their first scheduled shift so the system has time to send their welcome email.',
      'Deactivate, do not delete — keeping inactive records preserves timesheet history for compliance.',
      'Managers can be assigned to multiple locations; they will see all locations they manage in their dashboard.',
      'PINs should be shared verbally or via direct message, not written on shared notes near the kiosk.',
      'If an employee\'s name or email changes (e.g., after marriage), update their profile immediately to avoid login confusion.',
    ],
    troubleshooting: [
      {
        problem: 'New employee did not receive their welcome email',
        solution: 'Ask them to check their spam/junk folder. If not there, go to Staff Management → Edit the employee → Resend Invitation. Also verify the email address is correct.',
      },
      {
        problem: 'Employee cannot log in',
        solution: 'Check that the account is Active. If they forgot their password, direct them to the Forgot Password link on the login page. If the account is Inactive, reactivate it first.',
      },
      {
        problem: 'Employee is not appearing on the schedule',
        solution: 'Ensure the employee is assigned to the correct location. Go to their profile → Locations tab → confirm the location is listed and marked as Active.',
      },
      {
        problem: 'Employee says their PIN is not working at the kiosk',
        solution: 'Verify the PIN was set for the correct location — PINs are location-specific. Reset the PIN in Staff Management and share the new one with the employee.',
      },
    ],
    faq: [
      { q: 'What is the difference between Staff and Manager roles?', a: 'Staff accounts access only the Employee Portal — they see their schedule, timesheets, announcements, and documents. Manager accounts access the full management dashboard and can create schedules, review timesheets, and manage staff.' },
      { q: 'Can an employee be a manager at one location and staff at another?', a: 'Not in the current version. The role is account-wide. You can assign managers to multiple locations and they will have manager-level access at all of them.' },
      { q: 'How do I remove an employee completely?', a: 'Deactivate the account instead of deleting it. This preserves all timesheet and schedule history while immediately revoking login and kiosk access.' },
      { q: 'Is the PIN visible to managers?', a: 'No. PINs are encrypted at rest. Managers can reset a PIN but cannot view the current one.' },
      { q: 'Can an employee change their own password?', a: 'Yes. Employees can change their password from Account Security in the sidebar (managers/owners) or from My Profile in the Employee Portal (staff).' },
    ],
  },

  /* ─────────────────────────────────────────────
     EVA AI ASSISTANT
  ───────────────────────────────────────────── */
  'eva-ai': {
    title: 'Eva AI — Commands, Insights & Setup',
    description: 'Eva is a hybrid workforce assistant. Use instant commands without AI, access smart labor insights, or connect an AI provider for natural language answers.',
    icon: '✦',
    category: 'AI Assistant',
    sections: [
      { id: 'overview',        label: 'What is Eva' },
      { id: 'features',        label: 'What Eva Can Do' },
      { id: 'setup',           label: 'Supported Providers' },
      { id: 'guide',           label: 'Setup Steps' },
      { id: 'benefits',        label: 'Important Rules' },
      { id: 'ask-eva',         label: 'What You Can Ask Eva' },
      { id: 'eva-in-action',   label: 'Eva in Action' },
      { id: 'tips',            label: 'Provider Links & Tips' },
      { id: 'troubleshooting', label: 'Common Errors' },
      { id: 'faq',             label: 'FAQ' },
    ],

    evaFeatures: [
      {
        title: 'Instant Actions (No AI Required)',
        items: [
          'Post announcements instantly (announce:)',
          'Create shifts in seconds (shift:)',
          'Cancel scheduled shifts (cancel shift:)',
          'Create tasks (task:)',
          'Delete tasks (delete task:)',
        ],
      },
      {
        title: 'Smart Labor Insights',
        items: [
          'Detect high-cost employees',
          'Identify overtime-heavy staff',
          'Highlight overstaffed days',
          'Compare locations performance',
          'Estimate potential cost savings',
        ],
      },
      {
        title: 'Live Payroll Intelligence',
        items: [
          'Real-time shift cost preview',
          'Automatic break deductions',
          'Overtime + holiday stacking',
          'Accurate payroll breakdown',
          'Zero manual calculation',
        ],
      },
      {
        title: 'Command + AI Hybrid (Future Ready)',
        items: [
          'Deterministic commands (fast & reliable)',
          'Optional AI layer (natural language)',
          'No API required for core features',
          'No hallucinations on core operations',
          'Built for real operations',
        ],
      },
    ],

    evaScreenshots: [
      { src: 'https://restaff.b-cdn.net/Eva/eva3.jpg', alt: 'Eva Chat Commands' },
      { src: '/help/eva-insights.jpg',                 alt: 'Eva Insights Dashboard' },
    ],

    overview: `Eva is your AI workforce assistant built directly inside ReStaff. Eva helps owners and managers make faster, better decisions about labor costs, scheduling, and workforce risks — without leaving the app.\n\nEva is read-only. It analyzes your data and gives recommendations. It never modifies schedules, staff records, or any business data.\n\nEva works with multiple AI providers. You choose the one you prefer and supply your own API key. ReStaff does not charge for AI usage — you pay the provider directly based on your own usage.`,

    onboarding: `Eva supports 7 AI providers: OpenAI, Claude (Anthropic), Gemini (Google), Grok (xAI), DeepSeek, Kimi (Moonshot AI), and Z.ai (Zhipu AI). All require your own API key. Setup takes less than 5 minutes.`,

    setup: `Supported providers:\n\n• OpenAI\n• Claude (Anthropic)\n• Gemini (Google)\n• Grok (xAI)\n• DeepSeek\n• Kimi (Moonshot AI)\n• Z.ai (Zhipu AI)\n\nAll providers require your own API key. You can change provider at any time in Settings → AI Configuration.`,

    benefits: [
      'Eva is read-only — it cannot modify schedules, staff records, or any data',
      'Only account owners can configure the AI provider and API key',
      'Managers can use Eva after the owner configures it',
      'Staff users cannot access Eva — it is hidden from the staff portal',
      'API keys are encrypted before storage and never exposed to other users or logs',
      'You can switch AI providers at any time without losing your settings',
    ],

    askEvaQuestions: {
      subtitle: 'Ask Eva anything about your workforce, labor costs, or scheduling — get instant, data-driven answers.',
      image: 'https://restaff.b-cdn.net/Eva/eva3.jpg',
      microCopy: 'Eva analyzes your real data — schedules, timesheets, and payroll — to provide accurate insights in seconds.',
      groups: [
        {
          icon: '\uD83D\uDCB0',
          title: 'Cost Control',
          questions: [
            'Who is costing me the most this week, including overtime?',
            'Where am I overspending on labor this week?',
            'What caused my labor cost to increase compared to last week?',
            'How much am I over or under budget right now?',
            'Where am I losing the most money in staffing?',
            'How can I reduce labor cost by 10% next week?',
          ],
        },
        {
          icon: '\u26A0\uFE0F',
          title: 'Risks & Issues',
          questions: [
            'Which employees are close to overtime and should be reduced?',
            'Who worked the most overtime this week?',
            'Which employees forgot to clock out or have irregular shifts?',
            'What risks do you see in my current schedule?',
          ],
        },
        {
          icon: '\uD83D\uDCCA',
          title: 'Insights & Performance',
          questions: [
            'Which location is the least efficient based on labor vs hours worked?',
            'Which employees are underperforming based on hours vs output?',
            'What trends do you see in my payroll data this week?',
            'Which staff have the most inconsistent schedules?',
          ],
        },
        {
          icon: '\uD83D\uDCC5',
          title: 'Scheduling Optimization',
          questions: [
            'Which shifts are overstaffed right now?',
            'Where can I cut hours without affecting operations?',
            'Which days should I reduce staffing levels?',
            'Build me an optimized schedule for next week with minimal overtime',
            'Recommend changes to improve labor efficiency next week',
            'What is my projected labor cost for next week?',
          ],
        },
      ],
    },

    whenToUse: `Use Eva when you want a quick read on your current labor performance, need help deciding where to cut hours, or want to know which employees are at overtime risk before approving timesheets. Eva works best when shifts and timesheet data are loaded for the period you want to analyze.`,

    steps: [
      {
        title: 'Go to Settings → AI Configuration',
        desc: 'From the main sidebar, click Settings. Scroll to the AI Configuration section. This section is visible only to account owners.',
        tip: 'If you do not see the AI Configuration section, make sure you are logged in as the account owner.',
      },
      {
        title: 'Select your AI provider',
        desc: 'Use the provider dropdown to choose the AI service you want to use: OpenAI, Claude, Gemini, Grok, DeepSeek, Kimi, or Z.ai.',
        tip: 'Gemini is selected by default. Any provider works — choose based on what account you already have.',
      },
      {
        title: 'Open the provider setup page',
        desc: 'Click the "Open setup page" link below the dropdown. This takes you directly to the API key page for your chosen provider.',
        tip: 'Each provider link is specific to that provider. Make sure you are on the right page before creating a key.',
      },
      {
        title: 'Create an API key',
        desc: 'Sign in to your provider account and create a new API key. Copy the key — you will need it in the next step.',
        tip: 'Most providers offer a free tier that is sufficient for normal Eva usage. Check if billing needs to be enabled on your account.',
      },
      {
        title: 'Paste the API key into ReStaff',
        desc: 'Return to Settings → AI Configuration and paste your key into the API key field. The field shows dots by default — click "Show" to verify it was pasted correctly.',
        tip: 'Make sure there are no extra spaces before or after the key.',
      },
      {
        title: 'Click "Save Key"',
        desc: 'Click the orange "Save Key" button. Your key is encrypted and stored securely. The status badge will update to "Key saved — click Test to verify".',
        tip: 'Always save before testing. The Test Connection button verifies your saved settings, not unsaved changes.',
      },
      {
        title: 'Click "Test Connection"',
        desc: 'Click "Test Connection". ReStaff sends a short test request to your provider. If the key is valid, the status badge changes to "Connected" and Eva is ready to use.',
        tip: 'If the test fails, check that you selected the correct provider and that the key matches. Generate a new key if needed.',
      },
    ],

    tips: [
      'OpenAI keys start with "sk-" — get yours at: platform.openai.com/api-keys',
      'Anthropic (Claude) keys start with "sk-ant-" — get yours at: console.anthropic.com/settings/keys',
      'Gemini keys start with "AIzaSy" — get yours at: aistudio.google.com/app/apikey',
      'Grok (xAI) keys — get yours at: console.x.ai',
      'DeepSeek keys — get yours at: platform.deepseek.com/api_keys',
      'Kimi (Moonshot AI) keys — get yours at: platform.moonshot.cn/console/api-keys',
      'Z.ai (Zhipu AI) keys — get yours at: open.bigmodel.cn/usercenter/apikeys',
      'Always click "Save Key" before clicking "Test Connection" — the test uses your saved settings',
      'Make sure the provider dropdown matches the key you pasted — a Gemini key will not work with OpenAI selected',
      'If your provider shows a billing error, check that your account has an active payment method or free credits',
    ],

    troubleshooting: [
      {
        problem: '"Invalid API key" or 401 Unauthorized',
        solution: 'The key is incorrect, copied wrong, or the wrong provider is selected. Return to your provider dashboard, generate a new key, paste it in Settings, and save again. Make sure the provider dropdown matches the key type.',
      },
      {
        problem: '"404 Not Found" error',
        solution: 'This usually means a provider mismatch — the selected provider does not match the key format. Switch to the correct provider in the dropdown, save, and test again.',
      },
      {
        problem: '"429 Rate limit reached"',
        solution: 'Too many requests were sent in a short time. Wait a minute and try again. Eva has a built-in rate limit of 10 requests per 60 seconds to protect your API quota.',
      },
      {
        problem: '"Timeout / connection error"',
        solution: 'The provider API did not respond in time. Check your internet connection and try again. If the issue persists, the provider may be experiencing downtime — check their status page.',
      },
      {
        problem: '"No API key configured"',
        solution: 'No key has been saved yet. Go to Settings → AI Configuration, paste your key, click Save Key, then Test Connection.',
      },
      {
        problem: '"This does not look like a valid OpenAI key"',
        solution: 'OpenAI keys must start with "sk-". If your key does not match this format, you may have selected the wrong provider or copied an incomplete key.',
      },
      {
        problem: 'Eva does not appear for a manager',
        solution: 'Eva is available to owners and managers only. Confirm the user\'s role in Staff Management. If the role is "Staff", Eva will not be visible — this is by design.',
      },
    ],

    faq: [
      {
        q: 'Which AI provider should I choose?',
        a: 'Any provider works with Eva. Choose based on what account you already have. Gemini has a generous free tier. OpenAI (gpt-4o-mini) and DeepSeek are cost-effective. Kimi and Z.ai are good options if you are based in Asia and have existing accounts there.',
      },
      {
        q: 'Does ReStaff charge for Eva?',
        a: 'No. Eva uses your own API key from your chosen provider. ReStaff does not add any markup or charge for AI usage. You pay the provider directly based on your usage.',
      },
      {
        q: 'Can I switch providers later?',
        a: 'Yes. Go to Settings → AI Configuration, select a new provider from the dropdown, paste the new key, save, and test. Your previous key is replaced.',
      },
      {
        q: 'Is my API key secure?',
        a: 'Yes. Your API key is encrypted using industry-standard encryption before being stored. It is never exposed to other users, logs, or API responses.',
      },
      {
        q: 'Does Eva change any of my data?',
        a: 'No. Eva is strictly read-only. It analyzes data and provides recommendations, but it cannot create, edit, or delete schedules, staff records, timesheets, or any other business data.',
      },
      {
        q: 'Who can use Eva?',
        a: 'Eva is available to account owners and managers only. Staff users do not see the Eva button and cannot access Eva.',
      },
      {
        q: 'Does Eva remember my previous questions?',
        a: 'No. Eva does not retain any conversation history between sessions. Each question is answered independently. This keeps your data private and responses fast.',
      },
      {
        q: 'What is the difference between "Ask Eva", "Analyze with Eva", and "Scan for Risks"?',
        a: '"Ask Eva" (floating button) is for free-form questions with auto-fetched context from your recent shifts. "Analyze with Eva" in Executive Insights sends your current report metrics directly to Eva. "Scan for Risks" in Timesheets checks current entries for long shifts, overtime exposure, and missing clock-outs.',
      },
    ],
  },

  /* ─────────────────────────────────────────────
     WEEKLY SCHEDULE TEMPLATES
  ───────────────────────────────────────────── */
  'schedule-templates': {
    title: 'Weekly Schedule Templates',
    description: 'Save and reuse weekly schedule patterns to speed up shift planning across locations.',
    icon: '📋',
    category: 'Core Features',
    sections: [
      { id: 'overview',        label: 'Overview' },
      { id: 'benefits',        label: 'Key Benefits' },
      { id: 'when-to-use',     label: 'When to Use' },
      { id: 'guide',           label: 'Step-by-Step' },
      { id: 'tips',            label: 'Tips' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `Schedule Templates let managers save a full week of shifts as a reusable template. When starting a new week, load a template to populate the schedule grid instantly — then make adjustments as needed. Templates are unassigned by default, so you control who works each shift.`,
    benefits: [
      'Save any week as a reusable shift template',
      'Load a template into a new week in one click',
      'Templates are unassigned by default — assign employees after loading',
      'Only overwrites draft shifts — published schedules are protected',
      'Reusable across locations and weeks',
      'Eliminates repetitive manual shift creation',
    ],
    whenToUse: `Use templates when your weekly staffing pattern is consistent. Save your best week as a template once, then load it every week to skip manual shift entry.`,
    steps: [
      {
        title: 'Build a Schedule',
        desc: 'Create a full week of shifts in the Schedule module. Add all the shifts with the times and roles you need.',
        tip: 'Focus on getting the times and roles right — you can assign employees after loading.',
      },
      {
        title: 'Save as Template',
        desc: 'Click the Template button at the top of the schedule, then "Save Current Week as Template". Give the template a descriptive name.',
        tip: 'Name templates by pattern: "Weekday Standard", "Weekend Rush", "Holiday Reduced".',
      },
      {
        title: 'Load a Template',
        desc: 'Navigate to a future week, click Template → Load Template, and select one from your saved list. All shifts from the template populate the week.',
        tip: 'Loading a template only fills empty/draft cells — it does not overwrite published shifts.',
      },
      {
        title: 'Assign Employees',
        desc: 'After loading, click each shift to assign the employee for that week. Publish when ready.',
        tip: 'Templates are intentionally unassigned so you can adjust for availability each week.',
      },
    ],
    tips: [
      'Save 2–3 templates for different staffing patterns: regular weeks, busy periods, and reduced weeks.',
      'Templates work across locations — create one pattern and load it at any branch.',
      'After loading a template, use Copy Last Week for employee assignments from the previous week.',
      'Review templates quarterly and update them as your staffing needs evolve.',
    ],
    faq: [
      { q: 'Do templates include employee assignments?', a: 'No. Templates save shift times and roles only. Employees are assigned manually after loading to account for weekly availability changes.' },
      { q: 'Will loading a template delete my existing shifts?', a: 'Only draft (unpublished) shifts are overwritten. Published shifts remain untouched.' },
      { q: 'Can I share templates across locations?', a: 'Yes. Saved templates are available organization-wide and can be loaded at any location.' },
      { q: 'How many templates can I save?', a: 'There is no limit on the number of saved templates.' },
    ],
  },

  /* ─────────────────────────────────────────────
     MULTI-LOCATION MANAGEMENT
  ───────────────────────────────────────────── */
  'multi-location': {
    title: 'Multi-Location Management',
    description: 'Run all your business locations from a single ReStaff account with full data isolation.',
    icon: '🏢',
    category: 'Administration',
    sections: [
      { id: 'overview',        label: 'Overview' },
      { id: 'benefits',        label: 'Key Benefits' },
      { id: 'when-to-use',     label: 'When to Use' },
      { id: 'guide',           label: 'Step-by-Step' },
      { id: 'tips',            label: 'Tips' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `ReStaff supports multiple business locations under one account. Each location has its own schedules, timesheets, staff assignments, and kiosk. Managers see only the locations they are assigned to, while owners have visibility across all branches.`,
    benefits: [
      'Manage all branches from a single dashboard',
      'Location-based scheduling — each branch has its own weekly grid',
      'Full data isolation between locations for clean reporting',
      'Assign employees to one or multiple locations',
      'Location filter on every page for focused workflows',
      'Executive Insights aggregates data across all locations',
    ],
    whenToUse: `Set up multi-location management when opening a new branch, consolidating operations, or onboarding a franchise. Add the location in Settings, assign staff, and start scheduling immediately.`,
    steps: [
      {
        title: 'Add a Location',
        desc: 'Go to Settings → Locations → Add Location. Enter the branch name, address, and set an optional weekly labor budget.',
        tip: 'Use clear, short names like "Downtown" or "Airport Rd" — they appear in filters across the app.',
      },
      {
        title: 'Assign Employees',
        desc: 'In Staff Management, open each employee and assign them to the new location. Employees can be assigned to multiple branches.',
        tip: 'An employee must be assigned to a location before they appear on that location\'s schedule or kiosk.',
      },
      {
        title: 'Switch Between Locations',
        desc: 'Use the location filter at the top of any page (Schedule, Timesheets, Reports) to switch between branches or view all at once.',
        tip: 'The "All Locations" option gives a combined view — useful for owners monitoring the entire business.',
      },
      {
        title: 'Set Location Budgets',
        desc: 'In Settings → Locations → Edit, set a weekly labor budget. This enables the Budget vs. Actual comparison in Executive Insights.',
        tip: 'Budgets are per-location and per-week. Set them once and they carry forward.',
      },
    ],
    tips: [
      'Each location has its own kiosk URL — bookmark it on the tablet at each branch for quick access.',
      'Assign a dedicated manager to each location for accountability.',
      'Use Executive Insights to compare performance across all locations at a glance.',
      'When an employee transfers to a new branch, update their location assignment in Staff Management immediately.',
    ],
    faq: [
      { q: 'Is there a limit on the number of locations?', a: 'No hard limit. You can add as many locations as your business requires.' },
      { q: 'Can a manager see all locations?', a: 'Managers only see locations they are assigned to. Owners see all locations by default.' },
      { q: 'Are timesheets separated by location?', a: 'Yes. Each location has its own timesheet entries. Use the location filter to switch views or see all at once.' },
      { q: 'Can I delete a location?', a: 'Locations can be deactivated but not deleted, preserving historical schedule and timesheet data.' },
    ],
  },

  /* ─────────────────────────────────────────────
     TASKS & CHECKLISTS
  ───────────────────────────────────────────── */
  'tasks': {
    title: 'Tasks & Checklists',
    description: 'Assign operational tasks to staff, track completion, and keep daily workflows on track.',
    icon: '✅',
    category: 'Workforce Management',
    sections: [
      { id: 'overview',        label: 'Overview' },
      { id: 'benefits',        label: 'Key Benefits' },
      { id: 'when-to-use',     label: 'When to Use' },
      { id: 'guide',           label: 'Step-by-Step' },
      { id: 'tips',            label: 'Tips' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `The Tasks module helps managers assign and track daily operational work. Create tasks, assign them to specific employees, set due dates, and monitor completion — all from one page. Staff see their assigned tasks in their portal and mark them as done.`,
    benefits: [
      'Assign tasks to individual staff members',
      'Track completion status in real time',
      'Daily operational checklists for opening/closing routines',
      'All tasks visible to managers for accountability',
      'Employees mark tasks complete from their portal',
      'Keeps verbal instructions documented and trackable',
    ],
    whenToUse: `Use Tasks for daily checklists (opening/closing procedures), one-off assignments (inventory count, deep clean), and recurring operational workflows that need accountability.`,
    steps: [
      {
        title: 'Create a Task',
        desc: 'Go to Tasks in the sidebar. Click Add Task. Enter a title, description, and optional due date.',
        tip: 'Keep task titles short and action-oriented: "Restock cooler", "Submit inventory count".',
      },
      {
        title: 'Assign to an Employee',
        desc: 'Select the employee from the dropdown. The task appears in their portal immediately.',
        tip: 'Assign tasks during or before the shift so employees know what to do when they arrive.',
      },
      {
        title: 'Track Completion',
        desc: 'The Tasks page shows all tasks with their current status. Completed tasks are marked by the employee when finished.',
        tip: 'Review completed tasks at the end of each day to ensure nothing was missed.',
      },
    ],
    tips: [
      'Create a standard checklist for opening and closing shifts — assign it to the responsible employee each day.',
      'Use tasks for safety checks, cleaning routines, or equipment inspections.',
      'Follow up on overdue tasks quickly — they signal that a process is being missed.',
      'Keep task descriptions brief but specific enough that any employee can understand what to do.',
    ],
    faq: [
      { q: 'Can employees create their own tasks?', a: 'Tasks are created by managers and owners. Employees can view and complete tasks assigned to them.' },
      { q: 'Do tasks repeat automatically?', a: 'Currently tasks are created individually. For recurring work, create a new task each day or week as needed.' },
      { q: 'Can I see tasks for all locations?', a: 'Use the location filter to view tasks for a specific branch or all locations at once.' },
      { q: 'Who can see task completion status?', a: 'Managers and owners can see all task statuses. Employees see only their own assigned tasks.' },
    ],
  },

  /* ─────────────────────────────────────────────
     ANNOUNCEMENTS
  ───────────────────────────────────────────── */
  'announcements': {
    title: 'Announcements',
    description: 'Broadcast important updates to all staff or specific locations — no group chat required.',
    icon: '📢',
    category: 'Administration',
    sections: [
      { id: 'overview',        label: 'Overview' },
      { id: 'benefits',        label: 'Key Benefits' },
      { id: 'when-to-use',     label: 'When to Use' },
      { id: 'guide',           label: 'Step-by-Step' },
      { id: 'tips',            label: 'Tips' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `The Announcements module lets managers and owners broadcast messages to all staff or specific locations. Post policy changes, schedule reminders, holiday notices, or any important update — employees see it the next time they log in. Pin critical announcements to keep them at the top.`,
    benefits: [
      'Broadcast messages to all staff in one click',
      'Location-specific announcements for branch-level updates',
      'Pin important updates to keep them visible at the top',
      'Employees see announcements in their portal — no external tools needed',
      'Full history of all announcements for reference',
      'Replaces informal WhatsApp or email chains for staff communications',
    ],
    whenToUse: `Use Announcements for policy changes, schedule updates, holiday closures, safety reminders, team recognitions, or any message that all (or location-specific) staff should see.`,
    steps: [
      {
        title: 'Create an Announcement',
        desc: 'Go to Announcements in the sidebar. Click New Announcement. Enter a title and message body.',
        tip: 'Keep announcements short and actionable — staff are more likely to read concise updates.',
      },
      {
        title: 'Choose Target Audience',
        desc: 'Select whether to broadcast to all staff or only employees at a specific location.',
        tip: 'Use location-specific announcements for branch-level changes like hours or local policies.',
      },
      {
        title: 'Pin if Important',
        desc: 'Toggle the pin option to keep an announcement at the top of the list for all employees.',
        tip: 'Only pin truly critical updates — too many pinned items reduce visibility.',
      },
      {
        title: 'Staff Reads Announcement',
        desc: 'Employees see new announcements on their portal dashboard. Pinned announcements appear at the top.',
        tip: 'Pair time-sensitive announcements with a verbal reminder during shift handoff.',
      },
    ],
    tips: [
      'Post schedule changes as announcements so affected staff are notified even if they don\'t check the schedule immediately.',
      'Use announcements for positive updates too — employee recognitions and milestones boost morale.',
      'Archive old announcements periodically to keep the feed relevant.',
      'For urgent messages, combine an announcement with direct communication to ensure immediate awareness.',
    ],
    faq: [
      { q: 'Can employees respond to announcements?', a: 'Announcements are one-way broadcasts. For two-way communication, use direct messaging or in-person follow-ups.' },
      { q: 'Do employees get notified of new announcements?', a: 'Employees see new announcements when they log in. Push notifications may be available depending on your notification settings.' },
      { q: 'Can I edit an announcement after posting?', a: 'Yes. Click on any announcement to edit its content or update the target audience.' },
      { q: 'Is there a limit on announcements?', a: 'No. Post as many as needed — older announcements scroll down in the feed.' },
    ],
  },

  /* ─────────────────────────────────────────────
     BILLING & SUBSCRIPTION
  ───────────────────────────────────────────── */
  'billing': {
    title: 'Billing & Subscription',
    description: 'Manage your plan, view invoices, and update payment details — all from one page.',
    icon: '💳',
    category: 'Administration',
    sections: [
      { id: 'overview',        label: 'Overview' },
      { id: 'benefits',        label: 'Key Benefits' },
      { id: 'when-to-use',     label: 'When to Use' },
      { id: 'guide',           label: 'Step-by-Step' },
      { id: 'tips',            label: 'Tips' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `The Billing page gives account owners full control over their ReStaff subscription. View your current plan, upgrade or downgrade, manage payment methods, and access invoice history. Billing is powered by Stripe for secure, PCI-compliant payment processing.`,
    benefits: [
      'Upgrade or downgrade plans at any time',
      'Secure Stripe-powered payment processing',
      'View current plan status and renewal date',
      'Access full invoice history and download receipts',
      'Update payment methods without contacting support',
      'Transparent pricing — no hidden fees',
    ],
    whenToUse: `Visit Billing when you need to change your plan tier, update a credit card, check your next renewal date, or download an invoice for accounting.`,
    steps: [
      {
        title: 'Open Billing',
        desc: 'Click Billing in the sidebar. This page is only visible to account owners.',
        tip: 'If you don\'t see Billing in the sidebar, confirm your role is set to Owner.',
      },
      {
        title: 'View Current Plan',
        desc: 'The top section shows your active plan, renewal date, and current billing cycle details.',
        tip: 'Check your plan before adding new locations — some plans have location or staff limits.',
      },
      {
        title: 'Upgrade or Downgrade',
        desc: 'Click Manage Subscription to change your plan. Select the new tier and confirm. Changes take effect on your next billing cycle.',
        tip: 'Upgrading is immediate. Downgrading takes effect at the end of the current billing period.',
      },
      {
        title: 'Update Payment Method',
        desc: 'Click Update Payment to add or change your credit card. Stripe handles all payment data securely.',
        tip: 'Update your payment method before it expires to avoid service interruptions.',
      },
    ],
    tips: [
      'Download invoices at the end of each month for clean bookkeeping.',
      'If your business is seasonal, consider downgrading during slow months and upgrading for peak periods.',
      'Stripe handles all payment security — ReStaff never stores your credit card numbers.',
      'Contact support if you need a custom plan or invoice adjustments.',
    ],
    faq: [
      { q: 'Who can access Billing?', a: 'Only account owners. Managers and staff do not see the Billing page.' },
      { q: 'Can I cancel my subscription?', a: 'Yes. Go to Billing → Manage Subscription → Cancel. Your account remains active until the end of the current billing period.' },
      { q: 'What payment methods are accepted?', a: 'All major credit and debit cards via Stripe. Some plans may support bank transfers — contact support for details.' },
      { q: 'Will I lose my data if I downgrade?', a: 'No. Downgrading reduces available features or limits but does not delete any existing data.' },
    ],
  },

  /* ─────────────────────────────────────────────
     MOBILE & APP EXPERIENCE
  ───────────────────────────────────────────── */
  'mobile-app': {
    title: 'Mobile & App Experience',
    description: 'Use ReStaff on any device — installable as a PWA on iOS and Android, no app store needed.',
    icon: '📱',
    category: 'Getting Started',
    sections: [
      { id: 'overview',        label: 'Overview' },
      { id: 'benefits',        label: 'Key Benefits' },
      { id: 'when-to-use',     label: 'When to Use' },
      { id: 'guide',           label: 'Setup' },
      { id: 'tips',            label: 'Tips' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `ReStaff is a Progressive Web App (PWA) that works on iOS, Android, and desktop. Install it directly from the browser — no app store download required. The app syncs in real time, so schedules, clock events, and announcements are always up to date.`,
    benefits: [
      'Works on iOS, Android, and desktop browsers',
      'Installable as a PWA — appears as a native app on your home screen',
      'No app store required — install directly from the browser',
      'Real-time sync across all devices',
      'Responsive design adapts to any screen size',
      'Offline-ready for basic functionality',
    ],
    whenToUse: `Install the PWA on kiosk tablets for Time Clock access, on manager phones for on-the-go schedule edits, and on employee devices for checking shifts and clocking in remotely.`,
    steps: [
      {
        title: 'Open ReStaff in Your Browser',
        desc: 'Visit your ReStaff URL (e.g., getrestaff.co) in Chrome (Android) or Safari (iOS).',
        tip: 'Use Chrome on Android and Safari on iOS for the best PWA install experience.',
      },
      {
        title: 'Install on Android',
        desc: 'In Chrome, tap the three-dot menu → "Add to Home Screen" or tap the install banner if it appears. The app icon will be added to your home screen.',
        tip: 'After installing, the app opens in its own window without browser bars — just like a native app.',
      },
      {
        title: 'Install on iOS',
        desc: 'In Safari, tap the Share button (square with arrow) → "Add to Home Screen". Name it and tap Add.',
        tip: 'iOS requires Safari for PWA installation. Chrome on iOS does not support PWA install.',
      },
      {
        title: 'Use on Desktop',
        desc: 'In Chrome or Edge, click the install icon in the address bar or go to Menu → "Install ReStaff". The app opens in its own window.',
        tip: 'Pin the app to your taskbar for quick access on desktop.',
      },
    ],
    tips: [
      'Install the PWA on kiosk tablets and lock the browser to prevent staff from navigating away.',
      'Employees should install the app on their personal phone for quick schedule checks.',
      'The PWA updates automatically — no manual app store updates needed.',
      'For the best experience, keep your browser updated to the latest version.',
    ],
    faq: [
      { q: 'Is the PWA the same as the website?', a: 'Yes. The PWA is the same application — it just runs in its own window and can be launched from your home screen like a native app.' },
      { q: 'Does it work without internet?', a: 'Basic pages load from cache. Real-time features like clocking in and schedule edits require an active internet connection.' },
      { q: 'Is there a native app on the App Store or Play Store?', a: 'ReStaff is a PWA. It does not require a store download — install it directly from the browser for the same experience.' },
      { q: 'Can I receive push notifications?', a: 'Push notifications are supported on Android and desktop. iOS PWA push notification support depends on your iOS version (16.4+).' },
    ],
  },

  /* ─────────────────────────────────────────────
     PERMISSIONS & ROLES
  ───────────────────────────────────────────── */
  'permissions': {
    title: 'Permissions & Roles',
    description: 'Control what each user can see and do with Owner, Manager, and Staff roles.',
    icon: '🔐',
    category: 'Administration',
    sections: [
      { id: 'overview',        label: 'Overview' },
      { id: 'benefits',        label: 'Key Benefits' },
      { id: 'when-to-use',     label: 'When to Use' },
      { id: 'guide',           label: 'Role Breakdown' },
      { id: 'tips',            label: 'Tips' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `ReStaff uses three roles to control access: Owner, Manager, and Staff. Each role determines which pages, actions, and data a user can access. Roles are assigned during employee creation and can be changed at any time by an owner.`,
    benefits: [
      'Three clear roles: Owner, Manager, Staff',
      'Access control enforced on every page and API',
      'Owners have full control — billing, settings, all locations',
      'Managers handle day-to-day operations for their assigned locations',
      'Staff see only their own schedule, timesheets, and tasks',
      'Role changes take effect immediately',
    ],
    whenToUse: `Review permissions when onboarding new employees, promoting staff to manager, or auditing who has access to sensitive data like billing and payroll.`,
    steps: [
      {
        title: 'Owner',
        desc: 'Full access to everything: all locations, billing, settings, AI configuration, staff management, reports, and executive insights. Only owners can change subscription plans, manage API keys, and configure integrations.',
        tip: 'Limit owner accounts to business principals. Use manager accounts for operational leads.',
      },
      {
        title: 'Manager',
        desc: 'Access to Schedule, Timesheets, Staff, Tasks, Announcements, Reports, and Executive Insights for their assigned locations. Can approve timesheets, create shifts, manage documents, and use Eva AI.',
        tip: 'Assign managers to specific locations — they only see data for branches they manage.',
      },
      {
        title: 'Staff',
        desc: 'Access to the Employee Portal only: My Schedule, My Timesheets, Announcements, Documents, and Tasks. Staff can clock in/out, view their shifts, and complete assigned tasks. No access to management pages or Eva AI.',
        tip: 'Staff accounts are created automatically when you add an employee in Staff Management.',
      },
    ],
    tips: [
      'Use the principle of least privilege — give users only the access they need.',
      'When an employee is promoted to shift lead, consider upgrading to Manager role for schedule visibility.',
      'Review role assignments quarterly to ensure departing managers have been downgraded or deactivated.',
      'Eva AI is only available to Owners and Managers — this prevents staff from accessing business-level analytics.',
    ],
    faq: [
      { q: 'Can I create custom roles?', a: 'Currently ReStaff supports three fixed roles: Owner, Manager, and Staff. Custom roles are on the product roadmap.' },
      { q: 'Can a user have different roles at different locations?', a: 'Not in the current version. Roles are account-wide. A Manager is a Manager at all assigned locations.' },
      { q: 'How do I change someone\'s role?', a: 'Go to Staff Management → Edit the employee → change the Role dropdown → Save. The change takes effect immediately.' },
      { q: 'Can staff see other employees\' schedules?', a: 'Staff can only see their own schedule. They cannot view other employees\' shifts, timesheets, or personal data.' },
    ],
  },

  /* ─────────────────────────────────────────────
     OVERTIME & HOLIDAY PAY RULES
  ───────────────────────────────────────────── */
  /* ─────────────────────────────────────────────
     LABOR DASHBOARD
  ───────────────────────────────────────────── */
  'labor-dashboard': {
    title: 'Labor Dashboard',
    description: 'Visualize labor spending, track hours worked, and identify cost inefficiencies — filtered by date range and location.',
    icon: '📊',
    category: 'Analytics',
    screenshot: '/help/labor-dashboard.jpg',
    sections: [
      { id: 'overview',        label: 'Overview' },
      { id: 'benefits',        label: 'Key Benefits' },
      { id: 'when-to-use',     label: 'When to Use' },
      { id: 'guide',           label: 'Step-by-Step' },
      { id: 'tips',            label: 'Tips' },
      { id: 'troubleshooting', label: 'Troubleshooting' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `The Labor Dashboard is your operational spending lens. It pulls from approved timesheet data and your configured hourly wages to show exactly where your labor budget is going — broken down by employee, day, and location. Use it to understand where your money goes, spot expensive days, and take action before payroll closes.`,
    onboarding: `The Labor Dashboard is accessed from Dashboard → Labor tab. It updates whenever you change the date range or location filter. No setup is required — data flows in automatically from approved timesheets.`,
    benefits: [
      'Total Labor Cost KPI — actual dollar spend for any period',
      'Total Hours worked with employee headcount',
      'Average Cost per Hour (blended rate) across all staff',
      'Highest Cost employee identified at a glance',
      'Cost per Employee bar chart — spot outliers instantly',
      'Estimated Daily Labor Cost trend line — see which days spike',
      'Date range presets: This Week, Last Week, Bi-weekly, This Month, Last Month',
      'Custom date range picker for any pay period',
      'Location filter — drill into a single branch or view all',
      'Export CSV — one click, full data for any filtered period',
    ],
    whenToUse: `Use the Labor Dashboard weekly to review labor spend before payroll, monthly to compare locations or trends, and any time you want to understand where overtime or high costs are coming from. Filter by location when investigating a specific branch.`,
    steps: [
      {
        title: 'Open the Labor Dashboard',
        desc: 'Go to Dashboard in the sidebar, then click the Labor tab at the top of the page. The dashboard loads automatically with the current week\'s data.',
        tip: 'The Labor tab is only visible to owners and managers. Staff accounts see the Employee Portal instead.',
      },
      {
        title: 'Select a Date Range',
        desc: 'Use the preset buttons to select a period: This Week, Last Week, Bi-weekly, This Month, or Last Month. For a custom period (e.g., a specific pay cycle), click Custom and choose start and end dates.',
        tip: 'Match the date range to your payroll cycle for the most relevant comparison.',
      },
      {
        title: 'Filter by Location',
        desc: 'Use the Location dropdown to narrow the data to a single branch. The default is All Locations — switching to a specific location filters all KPI cards, charts, and Eva Insights simultaneously.',
        tip: 'Compare locations by switching back and forth — the KPI cards update instantly.',
      },
      {
        title: 'Read the KPI Cards',
        desc: 'The four summary cards at the top show: Total Labor (dollar spend), Total Hours with employee count, Avg Cost/Hour (blended wage rate), and Highest Cost employee. These update with every filter change.',
        tip: 'A high Avg Cost/Hour combined with a specific "Highest Cost" employee is a strong signal to check Eva Insights for a recommendation.',
      },
      {
        title: 'Analyze the Charts',
        desc: 'The Cost per Employee bar chart shows total labor cost per person — bars above average appear highlighted. The Estimated Daily Labor Cost trend shows which specific days had the highest spend. Use both charts to identify scheduling patterns.',
        tip: 'A spike in the daily trend often corresponds to a day where overtime accumulated or extra staff were added.',
      },
      {
        title: 'Export to CSV',
        desc: 'Click Export CSV (top-right corner of the Labor Overview header) to download a full data file for the current filter. The CSV includes employee names, hours, cost, and daily breakdown — ready for payroll review or accounting.',
        tip: 'Export immediately after setting your date range and location — the CSV contains exactly what is shown on screen.',
      },
    ],
    tips: [
      'Run the Labor Dashboard every Friday with "Last Week" selected to confirm spending before payroll closes.',
      'Use "This Month" to track cumulative labor spend in real time throughout the month.',
      'The Avg Cost/Hour (blended rate) is your best single number to compare against your labor budget target.',
      'If Total Hours is higher than expected, check the daily trend for specific spike days and cross-reference with the Schedule.',
      'Use the Location filter + Eva Insights together — filter to a location and Eva will surface location-specific recommendations.',
      'The CSV export includes raw data for each employee — use it in Excel/Sheets for deeper custom analysis.',
    ],
    troubleshooting: [
      {
        problem: 'KPI cards show $0 or 0 hours',
        solution: 'The Labor Dashboard only includes approved timesheet entries for the selected date range. If entries are still Pending, go to Timesheets and approve them first.',
      },
      {
        problem: 'Cost per Employee shows $0 for some employees',
        solution: 'The labor cost calculation requires hourly wages to be set for each employee. Go to Staff Management, open the employee\'s profile, and set their hourly wage.',
      },
      {
        problem: 'Export CSV is empty',
        solution: 'The CSV exports exactly what is visible on screen. If the charts and KPIs show no data, the CSV will also be empty. Adjust the date range or location filter to show the correct period.',
      },
      {
        problem: 'Labor Dashboard tab is not visible',
        solution: 'The Labor tab appears on the Dashboard page for owners and managers only. If you are logged in as a staff member, you will see the Employee Portal instead. Contact your manager.',
      },
      {
        problem: 'Avg Cost/Hour seems too low or too high',
        solution: 'The blended rate is the average across all employees weighted by hours worked. If a high-wage employee worked many more hours than others in the period, the average will skew upward. This is expected behavior.',
      },
    ],
    faq: [
      { q: 'Does the Labor Dashboard include unapproved timesheets?', a: 'No. Only entries with Approved status are included. Pending or Rejected entries are excluded from all calculations.' },
      { q: 'What date ranges can I compare?', a: 'Use the preset buttons for common ranges (this week, last week, bi-weekly, this month, last month) or choose any custom start and end date using the Custom picker.' },
      { q: 'Can I see all locations at once?', a: 'Yes. The default view is All Locations, which aggregates data across every branch in your account. Use the Location filter to drill into a specific branch.' },
      { q: 'What does "Highest Cost" show?', a: 'The Highest Cost card shows the employee with the highest total labor spend (hours × wage) for the selected period. It is not cost per hour — it is total dollars paid to that employee in the period.' },
      { q: 'Does the CSV export include overtime breakdown?', a: 'The Labor Dashboard CSV includes total cost per employee for the selected period. For a full overtime and holiday pay breakdown, use the Payroll Summary export in the Timesheets module.' },
      { q: 'How is "Estimated Daily Labor Cost" calculated?', a: 'Daily cost = total hours worked on that day × each employee\'s configured hourly wage. It is based on approved timesheets only and requires employee wages to be set in Staff Management.' },
    ],
  },

  /* ─────────────────────────────────────────────
     EVA INSIGHTS (Deterministic Recommendations)
  ───────────────────────────────────────────── */
  'eva-insights': {
    title: 'Eva Insights',
    description: 'Eva is a hybrid workforce assistant combining deterministic insights, action commands, and an optional AI layer — no external AI required for core features.',
    icon: '💡',
    category: 'Analytics',
    screenshot: '/help/eva-insights.jpg',
    sections: [
      { id: 'overview',        label: 'What is Eva Insights' },
      { id: 'benefits',        label: 'Key Points' },
      { id: 'when-to-use',     label: 'When to Use' },
      { id: 'guide',           label: 'Understanding Each Rule' },
      { id: 'tips',            label: 'Tips' },
      { id: 'troubleshooting', label: 'Troubleshooting' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `Eva is a hybrid workforce assistant built into ReStaff.

It combines three layers:

1. Deterministic Insights Engine
Eva Insights analyzes your payroll and scheduling data using fixed business rules.
It does NOT rely on external AI APIs and produces consistent, repeatable results.

2. Action Command System
Managers can use Eva to instantly perform actions like:
• Posting announcements
• Creating shifts
• Creating tasks
• Canceling shifts

These actions are executed directly — no AI required.

3. Optional AI Layer (future-ready)
Eva is designed to support AI features such as natural language commands, explanations, and optimization suggestions.
This layer is optional and only activates if configured.`,
    onboarding: `Eva Insights appears automatically on the Labor Dashboard (Dashboard → Labor tab). It updates instantly when you switch date ranges or locations. No setup required.`,
    benefits: [
      'No setup required — works out of the box with your existing data',
      'No external AI, no API key, no chatbot — purely deterministic rules',
      'Updates instantly when you change date range or location filter',
      'Each recommendation includes a specific action and estimated dollar savings',
      'Max 3 recommendations — ranked by highest dollar impact first',
      'Confidence badge on each card: high (direct calculation) or medium (estimate)',
      'If no issues are found, shows: "Your operations look efficient this period"',
    ],
    whenToUse: `Check Eva Insights at the start of every week to see if any labor inefficiencies appeared in the previous period. Switch to "Last Month" for a full period view. Use the Location filter to see location-specific recommendations. Always review before approving payroll or publishing the next schedule.`,
    steps: [
      {
        title: 'Rule 1 — High Labor Cost',
        desc: 'Fires when an employee\'s cost per hour is more than 20% above the period average. The action tells you which employee to reduce or replace. The impact shows the estimated savings if that employee\'s hours were reduced to match the average rate.',
        tip: 'Example: "High labor cost detected — Reduce shifts for John or replace with lower-cost staff. Est. savings: $161 — John costs 57% more than avg ($20.23/hr vs $12.90/hr)"',
      },
      {
        title: 'Rule 2 — Excess Overtime',
        desc: 'Fires when a specific employee\'s overtime hours exceed 25% of their total hours for the period. The action suggests splitting or redistributing their shifts. The impact estimates the overtime premium (hours × base rate × 0.5x assumed OT rate).',
        tip: 'Example: "Excess overtime usage — Split shifts or redistribute hours for John. Est. OT premium: $100 — 26% of John\'s hours are overtime"',
      },
      {
        title: 'Rule 3 — Overstaffed Day',
        desc: 'Fires when a single day\'s labor cost exceeds 120% of the daily average for the period. The action names the specific day and suggests reducing one staff member during low-demand hours. The impact is the dollar difference between that day\'s cost and the average.',
        tip: 'Example: "Overstaffed day detected — Reduce 1 staff during low-demand hours on Mar 18. Est. savings: $183 on Mar 18 (84% above daily avg)"',
      },
      {
        title: 'Rule 4 — Location Inefficiency',
        desc: 'Only appears when "All Locations" is selected. Fires when one location\'s cost per hour is more than 15% higher than another location. The action names the more expensive location and the savings estimate if it aligned to the lower-cost location\'s rate.',
        tip: 'Example: "Location inefficiency — Review staffing model at Downtown, costs 22% more per hour than Uptown. Est. savings: $240 if aligned to avg cost"',
      },
      {
        title: 'Rule 5 — Overtime Increasing',
        desc: 'Detects rising overtime costs within the period. If the period has 6+ days of data, it compares the average cost/hour in the first half vs. the second half. If the second half is 15%+ more expensive, it signals overtime accumulation. The action suggests adjusting schedules earlier in the week.',
        tip: 'Example: "Overtime increasing — Adjust schedule earlier in week to prevent overtime accumulation. Est. excess cost: $99 in latter half of this period"',
      },
    ],
    tips: [
      'Switch to "Last Month" to get the most data — more days means more accurate spike and trend detection.',
      'Filter by a specific location to focus Eva Insights on that branch only.',
      'Eva Insights updates instantly when you change filters — no page reload needed.',
      'The Confidence badge shows "high" for direct calculations (exact employee data) and "medium" for estimates (daily averages, period trends).',
      'If Eva shows "Your operations look efficient this period" — no rules fired. That is a good sign for that period.',
      'Eva Insights and the AI Eva chat (Ask Eva button) are completely separate — Eva Insights requires no setup or API key.',
    ],
    troubleshooting: [
      {
        problem: 'Eva Insights shows no recommendations even though I have data',
        solution: 'Eva Insights only fires when specific thresholds are exceeded. If no employee costs 20%+ above average, no overtime exceeds 25%, and no day spikes 20%+ above the daily average — the engine correctly shows "Your operations look efficient." Try a longer date range like "Last Month" to surface any historical patterns.',
      },
      {
        problem: 'The estimated savings seems too high or too low',
        solution: 'All impact numbers are estimates based on your current approved timesheet data and configured hourly wages. They assume the inefficiency is fully corrected — actual savings will depend on how schedules are adjusted. The amounts are directional, not accounting figures.',
      },
      {
        problem: 'Rule 4 (Location Inefficiency) never appears',
        solution: 'Rule 4 only activates when the Location filter is set to All Locations AND there are at least 2 locations with approved timesheet data and hourly wages configured. If you only have one active location or wages are missing, Rule 4 will not fire.',
      },
      {
        problem: 'Rule 5 (Overtime Increasing) never fires',
        solution: 'Rule 5 requires at least 6 days of data to detect a trend. Select "Last Month" or a custom range of at least 2 full weeks. It also requires wages to be set for cost-per-hour comparison.',
      },
      {
        problem: 'Eva Insights card is not visible on the Labor Dashboard',
        solution: 'The Eva Insights card appears between the KPI cards and the charts. If the Labor Dashboard shows no data (due to no approved timesheets), the card may show only the header with no recommendations. Approve timesheets for the selected period first.',
      },
    ],
    faq: [
      { q: 'Does Eva Insights use external AI?', a: 'Eva Insights itself does not use external AI. It is a deterministic rules engine.\n\nHowever, Eva as a system includes a command interface and is designed to support optional AI features in the future.' },
      { q: 'What can Eva do today?', a: 'Eva can:\n• Analyze labor costs and detect inefficiencies\n• Suggest actions based on real data\n• Create shifts, tasks, and announcements using commands\n• Help managers act faster without navigating multiple pages' },
      { q: 'What is the difference between Eva Insights and the Ask Eva chat button?', a: 'Eva Insights (on the Labor Dashboard) is a rules-based analysis engine — no AI, no setup, always-on. The Ask Eva chat button (floating orange button) is an AI chat assistant that requires an API key configured in Settings. They are completely separate features.' },
      { q: 'Why does Eva show a maximum of 3 recommendations?', a: 'Recommendations are capped at 3 and sorted by highest dollar impact. Showing too many dilutes focus — the top 3 by savings give you the highest-priority actions first.' },
      { q: 'Does Eva Insights remember previous analyses?', a: 'No. Eva recalculates from scratch every time you change a filter. There is no history — each view is a fresh calculation from current data.' },
      { q: 'Can staff members see Eva Insights?', a: 'No. Eva Insights is only visible to owners and managers on the Labor Dashboard. Staff accounts do not have access to the Labor tab.' },
      { q: 'Are the dollar savings estimates guaranteed?', a: 'No. They are model-based estimates using your current approved data and hourly wages. Actual savings depend on how scheduling changes are implemented. Use them as directional guidance, not financial forecasts.' },
      { q: 'What happens if I have no wages configured?', a: 'Rules that rely on dollar calculations (Rules 1, 2, 3, 4, 5 when cost data is available) will show reduced or no impact amounts. Rule 3 will fall back to showing excess hours instead of dollar savings. Set hourly wages in Staff Management for accurate financial estimates.' },
    ],
  },

  'overtime-holiday-pay': {
    title: 'Overtime & Holiday Pay Rules',
    description: 'Configure overtime thresholds, pay multipliers, and holiday dates for accurate payroll calculations.',
    icon: '💰',
    category: 'Workforce Management',
    screenshot: '/help/settings-overtime.jpg',
    sections: [
      { id: 'overview',        label: 'Overview' },
      { id: 'benefits',        label: 'Key Benefits' },
      { id: 'when-to-use',     label: 'When to Use' },
      { id: 'guide',           label: 'Step-by-Step' },
      { id: 'tips',            label: 'Tips' },
      { id: 'troubleshooting', label: 'Troubleshooting' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `The Overtime & Holiday Pay Rules feature lets owners configure two types of premium pay: overtime (hours worked beyond a daily threshold) and holiday pay (hours worked on designated holidays). Both are automatically applied to the Payroll Summary in Timesheets — no manual calculation needed. Settings are stored per account and take effect on the next payroll export.`,
    onboarding: `First time setting this up? Go to Settings → scroll to the "Overtime & Holiday Pay" card. Set your overtime threshold (default: 8 hours/day), overtime multiplier (default: 1.5×), and holiday multiplier (default: 2.0×). Add your holiday dates. Click Save.`,
    benefits: [
      'Automatic overtime calculation — no manual formulas',
      'Holiday pay applied to correct employees without extra steps',
      'Configurable daily overtime threshold per your jurisdiction',
      'Holiday multiplier applied independently from overtime rate',
      'Holiday Hours and Holiday Pay columns appear automatically in Payroll Summary',
      'All rules stored per account — consistent across all locations',
    ],
    whenToUse: `Configure overtime and holiday rules before running payroll for any period that includes overtime shifts or public holidays. Check Settings at the start of each year to update your holiday calendar.`,
    steps: [
      {
        title: 'Open Settings',
        desc: 'Click Settings in the sidebar (owner access required). Scroll down past Business Settings to the "Overtime & Holiday Pay" card.',
        screenshot: '/help/settings-overtime.jpg',
        tip: 'Only account owners can modify payroll rules. Managers can view the Payroll Summary but cannot change the thresholds.',
      },
      {
        title: 'Set the Overtime Threshold',
        desc: 'Enter the number of daily hours after which overtime pay applies in the "Daily Overtime After (hours)" field. The default is 8 hours. Any shift hours beyond this threshold will be calculated at the overtime multiplier rate.',
        tip: 'Check your local labor regulations. Some jurisdictions use weekly overtime thresholds — if so, contact support for custom configuration.',
      },
      {
        title: 'Set the Overtime Multiplier',
        desc: 'Enter the pay multiplier for overtime hours in the "Overtime Multiplier" field. The default is 1.5× (time and a half). For example, if an employee earns $20/hr and works 10 hours, the 2 overtime hours are paid at $30/hr.',
        tip: 'Common values: 1.5× for time-and-a-half, 2.0× for double time. Enter as a decimal (e.g., 1.5).',
      },
      {
        title: 'Set the Holiday Pay Multiplier',
        desc: 'Enter the pay multiplier for hours worked on a designated holiday in the "Holiday Pay Multiplier" field. The default is 2.0×. Holiday pay takes priority over overtime — if a shift falls on a holiday AND exceeds the overtime threshold, the holiday multiplier is used for all hours.',
        tip: 'Holiday pay overrides overtime pay for the entire shift, not just overtime hours.',
      },
      {
        title: 'Add Holiday Dates',
        desc: 'Click "+ Add Holiday" to define holiday dates. Enter a name (e.g., "Christmas Day") and the date. You can add as many holidays as needed. These dates are checked against each shift\'s date in the Payroll Summary.',
        tip: 'Add your full year\'s holiday calendar at once at the start of the year to avoid missing any dates.',
      },
      {
        title: 'Save and Verify in Payroll Summary',
        desc: 'Click "Save Payroll Settings". Then navigate to Timesheets → Payroll Summary tab. When any approved shifts fall on configured holiday dates, you will see "Holiday Hours" and "Holiday Pay" columns automatically appear in the summary table.',
        screenshot: '/help/timesheets.jpg',
        tip: 'If you do not see the Holiday columns, check that: (1) there are approved shifts, (2) the date range includes a configured holiday date.',
      },
    ],
    tips: [
      'Add all public holidays for the year in one session at the start of the year.',
      'Holiday pay takes priority over overtime — a shift on Christmas always uses the holiday multiplier, not the overtime rate.',
      'Use the Payroll Summary export after configuring holidays to verify calculations before running payroll.',
      'If a holiday falls on a day with no scheduled shifts, it has no effect — only actual worked hours are multiplied.',
      'Multipliers apply to the hourly wage set in Staff Management — ensure all employees have wages configured.',
    ],
    troubleshooting: [
      {
        problem: 'Holiday columns do not appear in Payroll Summary',
        solution: 'Holiday columns only appear when there are approved timesheet entries for dates that match your configured holidays. Check: (1) Are there approved shifts? (2) Is the date range in Timesheets covering the holiday date? (3) Is the holiday date correctly saved in Settings?',
      },
      {
        problem: 'Overtime not being calculated',
        solution: 'Overtime only applies when an employee has an hourly wage set in Staff Management. Also ensure the shift duration actually exceeds the configured threshold. Check the daily threshold — if set to 8 hours, a 7.5-hour shift will not trigger overtime.',
      },
      {
        problem: 'Holiday pay is not applying correctly',
        solution: 'Verify the exact date entered in Settings matches the shift date in Timesheets. Dates must match exactly (year, month, day). Also confirm the shift status is "Approved" — pending or rejected entries are excluded from payroll calculations.',
      },
      {
        problem: 'Changes to multipliers are not reflected in existing Payroll Summary',
        solution: 'Payroll calculations are performed dynamically when you open the Payroll Summary. After saving new settings, navigate away and return to Timesheets → Payroll Summary to see the recalculated values.',
      },
    ],
    faq: [
      { q: 'Does holiday pay override overtime pay?', a: 'Yes. If a shift falls on a configured holiday date, the holiday multiplier is applied to all hours in that shift. The overtime threshold is ignored for holiday shifts.' },
      { q: 'Can I configure different rules per location?', a: 'Currently, overtime and holiday rules are set at the account level and apply to all locations. Per-location rules are on the product roadmap.' },
      { q: 'What happens if I add a holiday after running payroll?', a: 'Payroll calculations are always recalculated on demand. If you add a past holiday date, re-open the Payroll Summary with the relevant date range to see updated figures.' },
      { q: 'Do multipliers apply to all employees?', a: 'Yes, to all employees who have an hourly wage set. Employees without a configured wage will show $0 in holiday/overtime pay columns but the hours are still tracked.' },
      { q: 'Can I export holiday pay data?', a: 'Yes. The Payroll Summary export (CSV or Excel) includes Holiday Hours and Holiday Pay columns when applicable.' },
    ],
  },

  /* ─────────────────────────────────────────────
     PAYROLL — RUN PAYROLL
  ───────────────────────────────────────────── */
  'payroll-run': {
    title: 'Run Payroll',
    description: 'Generate payslips automatically from approved timesheets, review the data, then finalize and export.',
    icon: '▶',
    category: 'Payroll',
    screenshot: '/help/payroll-runs-table.jpg',
    sections: [
      { id: 'overview',        label: 'Overview' },
      { id: 'benefits',        label: 'Key Benefits' },
      { id: 'guide',           label: 'Step-by-Step' },
      { id: 'tips',            label: 'Tips' },
      { id: 'troubleshooting', label: 'Troubleshooting' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `Payroll in ReStaff auto-generates payslips from approved timesheet entries. You pick a pay period, click Run Payroll, review each employee's pay breakdown, then finalize. Once finalized, payslips are permanently locked and emailed to employees automatically.`,
    benefits: [
      'Zero manual calculation — hours, overtime, holiday pay all computed automatically',
      'Each payslip is a permanent snapshot — past records never change',
      'Payslips emailed to employees the moment you finalize',
      'Export the full run as CSV or download a one-page PDF summary',
      'Cash advances deducted automatically per configured repayment schedule',
    ],
    onboarding: `Before running payroll: make sure all employee timesheets for the period are approved in the Timesheets module, and each employee has an hourly wage set in Staff Management.`,
    steps: [
      {
        title: 'Approve timesheets',
        desc: 'Go to Timesheets → select the pay period date range → approve all entries. Only approved shifts are included in payroll. Unapproved shifts are silently skipped — this is the most common cause of a "missing" employee in payroll.',
        tip: 'Use "Bulk Approve" to approve all entries for a date range in one click.',
      },
      {
        title: 'Open the Payslips module',
        desc: 'Click "Payslips" in the left sidebar (dollar sign icon). Switch to the "Payroll Runs" tab if it is not already selected.',
      },
      {
        title: 'Click "Run Payroll"',
        desc: 'Click the orange "Run Payroll" button in the top-right of the Payroll Runs tab. A dialog appears asking for Period Start and Period End dates.',
        screenshot: '/help/run-payroll-dialog.jpg',
        tip: 'You cannot run payroll for a period that already has a finalized run. If a draft already exists for this period, delete it first then re-run.',
      },
      {
        title: 'Review generated payslips',
        desc: 'After clicking Run, the system generates payslips for every employee with approved hours. Click "View" on any run to inspect individual employee breakdowns including regular pay, overtime, deductions, and advances.',
      },
      {
        title: 'Finalize the run',
        desc: 'When everything looks correct, click "Finalize Run". This permanently locks all payslips, triggers automatic email delivery to every employee, and prevents any further changes.',
        tip: 'Only the account Owner can finalize payroll. Managers can view and export but cannot finalize.',
      },
      {
        title: 'Export or download',
        desc: 'After finalization, two export buttons appear: "Export CSV" downloads a spreadsheet of all employee pay data, and "Download Summary PDF" produces a one-page management report with totals.',
      },
    ],
    tips: [
      'Run payroll in draft first, review all numbers carefully, then finalize — you cannot undo finalization.',
      'Set up hourly wages for every employee in Staff Management before running payroll.',
      'If a run has 0 payslips, check that timesheets are approved for that period and employees.',
      'Use the Month Filter dropdown to quickly find past payroll runs.',
      'You can delete a draft run (balances restore automatically) and re-run if corrections are needed.',
    ],
    troubleshooting: [
      {
        problem: 'Payroll run shows 0 employees / 0 payslips',
        solution: 'Go to Timesheets and verify that at least one employee has approved shift entries within the selected period. Also verify each employee has an hourly wage set in Staff Management.',
      },
      {
        problem: '"A payroll run already exists for this period" error',
        solution: 'A draft or finalized run already covers the same period and tenant. If it is a draft, click the delete button on that run, then retry. If it is finalized, it is permanently locked — you cannot regenerate it.',
      },
      {
        problem: 'Employee is missing from payslips',
        solution: 'Check two things: (1) their timesheets for this period are approved — not just submitted; (2) they have an hourly_wage set in Staff Management. Either condition being false will exclude them.',
      },
      {
        problem: 'Cannot click "Finalize" — button is missing',
        solution: 'Finalize is restricted to the account Owner role. Log in as the Owner, or ask the Owner to finalize the run.',
      },
    ],
    faq: [
      { q: 'Can I edit a payslip after finalization?', a: 'No. Finalization permanently locks all records. This is intentional — it ensures historical payroll data is never altered retroactively. If a correction is needed, document it as an adjustment in the next payroll cycle.' },
      { q: 'What if I need to correct a finalized run?', a: 'You cannot modify a finalized run. The recommended approach is to note the correction and apply it as a manual adjustment item in the next period\'s payroll run using a Pay Rule.' },
      { q: 'Does running payroll affect the timesheets?', a: 'No. Running payroll reads timesheet data but does not modify it. Timesheets remain visible and unchanged after payroll is generated.' },
      { q: 'Can I run payroll for multiple periods at once?', a: 'No. Each payroll run covers one period. Run them separately — one for each pay period.' },
    ],
  },

  /* ─────────────────────────────────────────────
     PAYROLL — UNDERSTANDING PAYSLIPS
  ───────────────────────────────────────────── */
  'payslips-explained': {
    title: 'Understanding Payslips',
    description: 'Learn what each line on your payslip means — regular pay, overtime, deductions, and net pay.',
    icon: '📄',
    category: 'Payroll',
    screenshot: '/help/individual-payslip.jpg',
    sections: [
      { id: 'overview',        label: 'Overview' },
      { id: 'benefits',        label: 'Key Benefits' },
      { id: 'guide',           label: 'Payslip Breakdown' },
      { id: 'tips',            label: 'Tips' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `A payslip is a permanent record of an employee's pay for a given period. ReStaff automatically calculates every line from approved timesheet data at the moment payroll is generated — no manual entry required. Once finalized, the payslip is locked and can be downloaded as a PDF.`,
    benefits: [
      'Every line item shows exactly how pay was calculated',
      'Deductions, incentives, and leave pay all appear as separate line items',
      'PDF download available for every employee',
      'Historical payslips are permanently preserved — never recalculated retroactively',
      'Employees receive their payslip by email on finalization',
    ],
    steps: [
      {
        title: 'Regular Pay',
        desc: 'Calculated as: Regular Hours × Hourly Wage. Regular hours are all approved shift hours up to the daily overtime threshold (typically 8h/day). This is the base pay for the period.',
      },
      {
        title: 'Overtime Pay',
        desc: 'Calculated as: Overtime Hours × Hourly Wage × Overtime Multiplier. Hours worked beyond the daily threshold (configured in Settings → Payroll Settings) are overtime. The multiplier is typically 1.5× but is configurable.',
      },
      {
        title: 'Holiday Pay',
        desc: 'Calculated as: Holiday Hours × Hourly Wage × Holiday Multiplier. If a shift falls on a configured holiday date, the holiday multiplier applies to all hours in that shift. Configure holiday dates in Settings → Payroll Settings.',
      },
      {
        title: 'Leave Pay',
        desc: 'Paid leave approved in the Leave module appears as a separate earning line: "Leave Pay — [Type]". The amount is the configured daily leave pay rate × days taken.',
      },
      {
        title: 'Incentives',
        desc: 'Incentive distributions from the Incentive Pools module appear as individual earning lines: "Incentive — [Pool Name]". The amount is proportional to the employee\'s hours in the pool period.',
      },
      {
        title: 'Deductions',
        desc: 'Configured deductions (health, pension, etc.) appear as negative items. Deductions can be fixed amounts or percentages of gross pay. Cash advance repayments also appear here.',
      },
      {
        title: 'Gross Pay',
        desc: 'The sum of all earnings: Regular Pay + Overtime + Holiday Pay + Leave Pay + Incentives + any positive Pay Rules.',
      },
      {
        title: 'Net Pay',
        desc: 'Gross Pay minus all deductions. This is the amount the employee actually receives.',
      },
    ],
    tips: [
      'Click "View" on any payslip to see the full line-by-line breakdown before finalizing.',
      'Download the payslip PDF from the payslip detail view — it mirrors the on-screen breakdown.',
      'Employees can view their own payslips under "My Payslips" in the Payslips module.',
      'Each line item records exactly how it was calculated (rate, hours, multiplier) so any number can be traced back to its source.',
    ],
    faq: [
      { q: 'Where can an employee view their payslip?', a: 'Employees see their own payslips under Payslips → My Payslips tab. They can also download a PDF from there.' },
      { q: 'Can a payslip amount change after it is generated?', a: 'No. Once generated, payslips are snapshots. Even if you change an employee\'s wage, hourly rate, or deduction rules after generation, existing payslips are unaffected.' },
      { q: 'What if a line item is missing?', a: 'Missing earnings usually mean the underlying data was not present when payroll was generated. For example, a deduction missing means the employee deduction record was not set up before payroll was run. Delete the draft and re-run after correcting the data.' },
    ],
  },

  /* ─────────────────────────────────────────────
     PAYROLL — CASH ADVANCES
  ───────────────────────────────────────────── */
  'cash-advances': {
    title: 'Cash Advances',
    description: 'Learn how employee cash advances are tracked, deducted from payroll automatically, and how balances decrease over time.',
    icon: '💳',
    category: 'Payroll',
    sections: [
      { id: 'overview',        label: 'Overview' },
      { id: 'benefits',        label: 'Key Benefits' },
      { id: 'guide',           label: 'How It Works' },
      { id: 'tips',            label: 'Tips' },
      { id: 'troubleshooting', label: 'Troubleshooting' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `Cash advances are short-term amounts given to employees that are automatically repaid by deducting a set amount from each subsequent payroll run. ReStaff tracks the full advance balance, calculates the deduction per payroll, and decrements the balance automatically — no manual tracking required.`,
    benefits: [
      'Fully automatic deduction — no manual reminder or calculation needed',
      'Deduction amount is configurable: full balance or a fixed amount per payroll cycle',
      'Balance is tracked in real time and visible before each payroll run',
      'Each deduction is stored as a snapshot in the payslip — fully auditable',
      'If a draft payroll run is deleted, advance balances are automatically restored',
    ],
    whenToUse: `Use cash advances whenever an employee requests an early payment against future wages. Set the advance amount in the Cash Advances section and the system will handle deductions automatically each pay cycle.`,
    steps: [
      {
        title: 'How an advance is created',
        desc: 'A cash advance record is created by an admin (Owner or Manager) in the employee\'s record. It includes the total amount, the remaining balance (starts equal to the total), and an optional "deduction per payroll" amount.',
      },
      {
        title: 'Deduction per payroll',
        desc: 'If "deduction per payroll" is set (e.g., $50/cycle), the system deducts exactly $50 from each payroll until the balance reaches $0. If no per-payroll amount is set, the full remaining balance is deducted in the first payroll run.',
        tip: 'Setting a per-payroll amount prevents a large deduction from leaving an employee with very little net pay.',
      },
      {
        title: 'Automatic deduction during payroll generation',
        desc: 'When payroll is generated (Run Payroll), the system fetches all active advances (remaining balance > 0) for each employee. The calculated deduction appears as a line item: "Cash Advance Repayment" under Deductions on the payslip.',
      },
      {
        title: 'Balance decreases automatically',
        desc: 'After each payroll run, the advance\'s remaining balance is reduced by the deducted amount. The next payroll cycle will deduct from the updated (lower) balance.',
        tip: 'If an employee has multiple advances, each gets its own deduction line item on the payslip.',
      },
      {
        title: 'Balance restored on draft delete',
        desc: 'If you delete a draft payroll run (before finalizing), all advance balances deducted in that run are automatically restored. This allows you to re-run payroll safely without double-deducting.',
      },
    ],
    tips: [
      'Always set a "deduction per payroll" amount to spread the repayment across multiple cycles.',
      'Review active advances before running payroll so deductions are expected.',
      'Multiple advances per employee are all processed in one payroll run — each appears as a separate line.',
      'Advance balances are never reduced twice for the same run — deleting a draft restores the balance.',
    ],
    troubleshooting: [
      {
        problem: 'Cash advance deduction is not appearing in payslip',
        solution: 'Check that the advance has remaining_balance > 0. An advance with zero balance is skipped. Also verify the advance is assigned to the correct employee and tenant.',
      },
      {
        problem: 'Advance balance was deducted but the payroll run was deleted',
        solution: 'If the deleted run was a draft, the balance is automatically restored. If it was finalized, the deduction is permanent — it cannot be reversed. Create a new advance entry for the difference if needed.',
      },
      {
        problem: 'Employee net pay went negative after advance deduction',
        solution: 'ReStaff applies the full deduction even if net pay goes negative. To avoid this, set a lower "deduction per payroll" amount so the deduction does not exceed expected earnings.',
      },
    ],
    faq: [
      { q: 'Can I stop a cash advance mid-repayment?', a: 'Set the remaining_balance to 0 in the advance record. The system will skip it on the next payroll run.' },
      { q: 'What happens when the balance reaches zero?', a: 'The advance is automatically skipped in future payroll runs. No deduction will appear — the employee is fully repaid.' },
      { q: 'Can an employee have multiple advances at once?', a: 'Yes. Each advance is tracked separately and each generates its own payslip deduction line item.' },
    ],
  },

  /* ─────────────────────────────────────────────
     PAYROLL — EMAIL PAYSLIPS
  ───────────────────────────────────────────── */
  'payslip-emails': {
    title: 'Email Payslips',
    description: 'Understand how payslips are automatically emailed to employees on finalization, and how to resend failed deliveries.',
    icon: '✉',
    category: 'Payroll',
    screenshot: '/help/payroll-email-status.jpg',
    sections: [
      { id: 'overview',        label: 'Overview' },
      { id: 'benefits',        label: 'Key Benefits' },
      { id: 'guide',           label: 'How Email Delivery Works' },
      { id: 'tips',            label: 'Tips' },
      { id: 'troubleshooting', label: 'Troubleshooting' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `When a payroll run is finalized, ReStaff automatically generates a PDF payslip for each employee and emails it as an attachment. Delivery happens in the background — you do not need to wait. The payroll runs table shows an email status icon so you can see delivery results at a glance.`,
    benefits: [
      'Fully automatic — employees receive payslips the moment finalization completes',
      'PDF attachment mirrors the on-screen payslip breakdown exactly',
      'Email status indicator shows success, partial failure, or not-yet-sent at a glance',
      'Resend Failed button lets you retry only failed recipients without re-sending to successful ones',
      'Auto-send can be turned off in Settings if manual distribution is preferred',
    ],
    steps: [
      {
        title: 'Automatic send on finalization',
        desc: 'When the Owner clicks "Finalize Run", the system immediately finalizes the payroll and queues a background email job. Each employee receives an email with subject "Your Payslip — [period_start] to [period_end]" and a PDF attachment.',
      },
      {
        title: 'Reading the email status icon',
        desc: 'In the Payroll Runs table, the Email column shows three possible icons: a grey minus icon means the run is not yet finalized; a green checkmark means all emails were delivered successfully; an amber warning icon means at least one email failed.',
        screenshot: '/help/payroll-email-status.jpg',
        tip: 'Hover over the icon to see a tooltip showing exactly how many were sent and how many failed.',
      },
      {
        title: 'Resending failed emails',
        desc: 'Open the payroll run detail (click View on the run). If the amber "Resend Failed (N)" button appears, click it. The system re-sends only to employees whose email failed — successful recipients are never re-emailed.',
        screenshot: '/help/payroll-resend-failed.jpg',
      },
      {
        title: 'Disabling auto-send',
        desc: 'Go to Settings → Payroll Settings → toggle "Auto-Send Payslips" to off. When disabled, finalizing a run will not send any emails. You can still download payslip PDFs manually and distribute them yourself.',
      },
    ],
    tips: [
      'Ensure every employee has a valid email address in Staff Management before running payroll.',
      'Check the email status icon after finalization — do not assume all emails succeeded.',
      'The "Resend Failed" button only appears when there are failed recipients — it is hidden when all succeeded.',
      'Emails are sent once per finalization. Re-finalizing is not possible — use "Resend Failed" for corrections.',
    ],
    troubleshooting: [
      {
        problem: 'Employee did not receive their payslip email',
        solution: 'Check the email status icon. If it shows amber (partial failure), use the "Resend Failed" button. Also verify the employee\'s email address is correct in Staff Management. Check their spam folder.',
      },
      {
        problem: 'All emails failed (failed=N, sent=0)',
        solution: 'This typically means there is an issue with the email provider or all employees have missing email addresses. Check Staff Management for email fields. Use the Resend Failed button after correcting the data.',
      },
      {
        problem: 'Auto-send is off but I want to send emails now',
        solution: 'There is no manual "Send All" button for runs where auto-send was disabled at finalization. Enable auto-send in Settings for future runs, or distribute payslip PDFs manually by downloading them from the run detail.',
      },
    ],
    faq: [
      { q: 'Can I send payslips to a different email than the one in the system?', a: 'No. Payslips are sent to the email address stored in the employee\'s Staff Management record. Update the email there before running payroll.' },
      { q: 'Do employees receive a copy if the email fails and I use Resend?', a: 'Yes. The Resend Failed button sends only to employees in the failed list. Once they are delivered, the failure count drops to 0.' },
      { q: 'Is there a risk of an employee receiving the email twice?', a: 'No. The system tracks which employees received successfully and excludes them from resend. Failed employees can be resent; successful ones cannot.' },
    ],
  },

  /* ─────────────────────────────────────────────
     PAYROLL — CSV EXPORT
  ───────────────────────────────────────────── */
  'csv-export': {
    title: 'CSV Export',
    description: 'Export a payroll run as a CSV file for use in Excel, accounting software, or external payroll systems.',
    icon: '↓',
    category: 'Payroll',
    screenshot: '/help/payslip-detail.jpg',
    sections: [
      { id: 'overview',        label: 'Overview' },
      { id: 'benefits',        label: 'Key Benefits' },
      { id: 'guide',           label: 'How to Export' },
      { id: 'tips',            label: 'Tips' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `The CSV export downloads every employee's payroll data for a finalized run into a single spreadsheet. All values come directly from stored payslip data — nothing is recalculated at export time. Open the file in Excel, Google Sheets, or import it directly into your accounting software.`,
    benefits: [
      'One-click download from the payroll run detail page',
      'Includes all pay components: regular, overtime, deductions, incentives, leave pay',
      'Values match exactly what is shown on screen — no recalculation',
      'Works with Excel, Google Sheets, QuickBooks, Xero, and any CSV-compatible tool',
      'Available only for finalized runs — ensures data accuracy',
    ],
    steps: [
      {
        title: 'Open the payroll run',
        desc: 'Go to Payslips → Payroll Runs → click "View" on the finalized run you want to export. The run detail page opens with a list of all payslips.',
      },
      {
        title: 'Click "Export CSV"',
        desc: 'In the run detail header, click the "Export CSV" button (outlined border button with a download arrow icon). The file downloads immediately — no loading screen.',
        tip: 'Export CSV is only visible on finalized runs. Draft runs cannot be exported.',
      },
      {
        title: 'Open in Excel or Google Sheets',
        desc: 'The downloaded file is named payroll_[period_start]_[period_end].csv. Open it directly in Excel or import it into Google Sheets via File → Import. All columns are pre-labelled.',
      },
    ],
    tips: [
      'Use the CSV for payroll journal entries in accounting software — the columns map cleanly to typical payroll GL codes.',
      'Total incentives and total leave pay are in separate columns so you can filter or sum them independently.',
      'For compliance or audit purposes, the CSV + PDF Summary together constitute a complete payroll record.',
      'You can export multiple pay periods by downloading each finalized run separately.',
    ],
    faq: [
      { q: 'What columns are included in the CSV?', a: 'employee_name, employee_id, period_start, period_end, regular_hours, overtime_hours, gross_pay, total_deductions, net_pay, total_incentives, total_leave_pay.' },
      { q: 'Are individual deduction line items in the CSV?', a: 'No. total_deductions is the aggregate sum of all deductions for the employee. For a full line-item breakdown, view the payslip on screen or download the PDF.' },
      { q: 'Can I export a draft run?', a: 'No. CSV export is restricted to finalized runs. This ensures the data is stable and will not change.' },
      { q: 'Can a Manager export the CSV?', a: 'Yes. Owners and Managers can both export the CSV. Staff cannot.' },
    ],
  },

  /* ─────────────────────────────────────────────
     PAYROLL — SUMMARY PDF
  ───────────────────────────────────────────── */
  'payroll-summary-pdf': {
    title: 'Payroll Summary PDF',
    description: 'Download a one-page management report summarizing total pay, hours, and deductions for a payroll run.',
    icon: '📋',
    category: 'Payroll',
    screenshot: '/help/payslip-detail.jpg',
    sections: [
      { id: 'overview',        label: 'Overview' },
      { id: 'benefits',        label: 'Key Benefits' },
      { id: 'guide',           label: 'How to Download' },
      { id: 'tips',            label: 'Tips' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `The Payroll Summary PDF is a one-page management report for a finalized payroll run. It shows aggregated totals across all employees — not individual payslip data. Use it for board reviews, budget reconciliation, or to keep a signed payroll record on file.`,
    benefits: [
      'One-page format: total employees, hours, gross pay, deductions, net pay',
      'Clean, print-ready layout — suitable for archiving or manager sign-off',
      'Includes an employee breakdown table listing each person\'s pay at a glance',
      'Incentive and leave pay totals shown separately',
      'Values match the CSV export exactly — no recalculation',
    ],
    steps: [
      {
        title: 'Open the finalized payroll run',
        desc: 'Go to Payslips → Payroll Runs → click "View" on a finalized run.',
      },
      {
        title: 'Click "Download Summary PDF"',
        desc: 'In the run header, click the dark "Download Summary PDF" button (file icon). The PDF downloads immediately. The file is named payroll_summary_[period_start]_[period_end].pdf.',
        tip: 'The button only appears on finalized runs. Draft runs cannot be exported.',
      },
      {
        title: 'Review the three-section report',
        desc: 'The PDF contains: (1) Overview — total employees, regular and overtime hours; (2) Financial Summary — gross pay, deductions, net pay; (3) Additional Pay — incentives and leave pay. An employee breakdown table lists every employee\'s individual totals.',
      },
    ],
    tips: [
      'Use the Summary PDF + CSV together as a complete payroll package for accounting and compliance.',
      'The Summary PDF is ideal for managers who need a high-level view without seeing individual employee details.',
      'Print and sign the Summary PDF as a physical payroll approval record — it includes a generation timestamp.',
    ],
    faq: [
      { q: 'Does the Summary PDF include individual payslips?', a: 'No. It shows aggregated totals and a per-employee row in the breakdown table. For full line-item payslip detail, download individual payslip PDFs from each employee row.' },
      { q: 'Who can download the Summary PDF?', a: 'Owners and Managers. Staff cannot.' },
      { q: 'Do values in the Summary PDF match the CSV?', a: 'Yes. Both read directly from stored payslip data — no recalculation occurs at download time.' },
    ],
  },

  /* ─────────────────────────────────────────────
     PAYROLL — COMMON ISSUES & TROUBLESHOOTING
  ───────────────────────────────────────────── */
  'payroll-troubleshooting': {
    title: 'Payroll — Common Issues',
    description: 'Diagnose and fix the most common problems encountered when running payroll, sending emails, or exporting data.',
    icon: '?',
    category: 'Payroll',
    sections: [
      { id: 'overview',        label: 'Overview' },
      { id: 'benefits',        label: 'Key Benefits' },
      { id: 'troubleshooting', label: 'Common Issues' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `This guide covers the most frequent payroll issues ReStaff users encounter — from empty runs and missing employees to stuck emails and locked records. Most problems have a one-step fix.`,
    benefits: [
      'Diagnose issues without contacting support',
      'Fix the most common root causes in under two minutes',
      'Understand why records become locked and what to do instead',
    ],
    troubleshooting: [
      {
        problem: 'Payroll run has 0 payslips / employees are missing',
        solution: 'The most common cause: timesheets for the period are not approved. Go to Timesheets, select the same date range, and approve all entries. Also check that the missing employee has an hourly_wage set in Staff Management — employees without a wage are excluded.',
      },
      {
        problem: 'Email status icon shows amber (partial failure)',
        solution: 'At least one employee email failed to deliver. Open the run detail and click the amber "Resend Failed (N)" button. This retries only the failed recipients. Check that employee email addresses in Staff Management are correct and not bounce-listed.',
      },
      {
        problem: 'Cannot delete a payroll run — "Permanently locked" error',
        solution: 'Only draft runs can be deleted. Finalized runs are permanently locked — this is by design to protect historical payroll integrity. If you finalized by mistake, document the correction and apply it as an adjustment in the next payroll cycle.',
      },
      {
        problem: 'Cash advance deduction is missing from payslip',
        solution: 'Check the advance record: if remaining_balance is already 0, no deduction is generated. Also verify the advance employee_id matches the payslip employee_id exactly.',
      },
      {
        problem: 'CSV export shows $0 for incentives / leave pay',
        solution: 'These columns are only populated if incentive distributions or paid leave requests existed with paid_amount > 0 at the time payroll was generated. They are not recalculated at export — if they were not present during the payroll run, they will be $0.',
      },
      {
        problem: '"A finalized payroll run already exists for this period" on re-run',
        solution: 'A finalized run for the same period already exists. Finalized runs cannot be regenerated — they are permanent. If a correction is needed, apply it in a new payroll cycle with an offsetting Pay Rule.',
      },
      {
        problem: 'Payslip amounts changed after I changed the employee\'s wage',
        solution: 'They did not. Payslips are immutable snapshots taken at generation time. A wage change after generation has no effect on existing payslips. Only future payroll runs will use the updated wage.',
      },
    ],
    faq: [
      { q: 'Can I reopen a finalized payroll run?', a: 'No. Finalization is permanent. This protects payroll integrity. Apply any corrections as adjustments in the next pay period.' },
      { q: 'Why is my payroll run showing wrong overtime hours?', a: 'Overtime is calculated based on your overtime threshold setting in Settings → Payroll Settings. Check that the "Overtime After X Hours/Day" value is correct.' },
      { q: 'An employee received two payslip emails', a: 'This should not happen — the system prevents duplicate sends. If it did, it may have been sent via Resend Failed after a false failure flag. No further action needed; email delivery is idempotent (same payslip content).' },
      { q: 'Can I export payroll data for an employee across multiple periods?', a: 'Not in one click — export each finalized run individually as CSV, then combine the spreadsheets. A cross-period export feature is on the product roadmap.' },
    ],
  },

  /* ─────────────────────────────────────────────
     TIMESHEET CSV EXPORT
  ───────────────────────────────────────────── */
  'timesheet-export': {
    title: 'Export your timesheets (CSV)',
    description: 'Download your timesheet data for payroll, accounting, or QuickBooks / Xero imports.',
    icon: '📥',
    category: 'Core Features',
    sections: [
      { id: 'overview', label: 'Overview' },
      { id: 'benefits', label: 'Two Export Modes' },
      { id: 'guide',    label: 'Step-by-Step' },
    ],
    overview: `Export your timesheets any time for payroll, accounting, or import into tools like QuickBooks or Xero. Two export modes are available so you can pick the right level of detail.`,
    benefits: [
      'Raw CSV — full operational data (hours, breaks, OT, compliance)',
      'Formatted CSV — clean accountant-ready columns',
      'Compatible with QuickBooks, Xero and any spreadsheet tool',
    ],
    steps: [
      {
        title: 'Open Timesheets',
        desc: 'Click "Timesheets" in the sidebar to open the current period\'s entries.',
      },
      {
        title: 'Pick a date range',
        desc: 'Use the preset chips (This Week, Last Week, etc.) or pick a custom Start and End date, then click Apply.',
      },
      {
        title: 'Click "Raw CSV" or "Formatted CSV"',
        desc: 'Raw CSV gives every operational field. Formatted CSV gives clean columns: employee, date, regular/OT/total hours, hourly rate, tips, gross pay, break compliance.',
        tip: 'Use Formatted CSV when sending data to your accountant or importing into QuickBooks / Xero.',
      },
      {
        title: 'Download the file',
        desc: 'The CSV downloads to your device immediately. The filename includes the date range so it\'s easy to track.',
      },
    ],
  },

  /* ─────────────────────────────────────────────
     INVITE STAFF
  ───────────────────────────────────────────── */
  'invite-staff': {
    title: 'How to invite staff to your team',
    description: 'Add team members so they can access schedules, clock in, and receive updates.',
    icon: '👥',
    category: 'Administration',
    screenshot: 'https://restaff.b-cdn.net/Screenshots/staff-list.jpg',
    sections: [
      { id: 'overview', label: 'Overview' },
      { id: 'benefits', label: 'Key Benefits' },
      { id: 'guide',    label: 'Step-by-Step' },
    ],
    overview: `Invite your team members so they can log in to ReStaff, see their schedule, clock in, and receive notifications.`,
    benefits: [
      'Staff get their own login in under a minute',
      'Each member is assigned to specific locations',
      'Optional PIN for fast kiosk clock-in',
    ],
    steps: [
      {
        title: 'Open Staff',
        desc: 'Click "Staff" in the sidebar to see your team directory.',
        screenshot: 'https://restaff.b-cdn.net/Screenshots/staff-list.jpg',
      },
      {
        title: 'Click "Add Staff"',
        desc: 'The Add Staff Member panel opens on the right with the invite form.',
        screenshot: 'https://restaff.b-cdn.net/Screenshots/invite-staff-modal.jpg',
      },
      {
        title: 'Enter name, email and role',
        desc: 'Fill in the full name and email address. Choose Staff or Manager as the role.',
      },
      {
        title: 'Assign at least one location',
        desc: 'Tick the locations this person should work at. This controls what they can see.',
      },
      {
        title: 'Click Add Staff',
        desc: 'The member is created and an invitation email with a login link is sent automatically.',
        tip: 'If they don\'t receive the email, ask them to check their spam folder.',
      },
    ],
  },

  /* ─────────────────────────────────────────────
     TRIAL & BILLING
  ───────────────────────────────────────────── */
  'trial-billing': {
    title: 'Free trial and billing explained',
    description: 'Understand your trial, subscription plan, and how to upgrade at any time.',
    icon: '💳',
    category: 'Administration',
    screenshot: 'https://restaff.b-cdn.net/Screenshots/billing-page.jpg',
    sections: [
      { id: 'overview', label: 'Overview' },
      { id: 'benefits', label: 'Key Benefits' },
      { id: 'guide',    label: 'Step-by-Step' },
    ],
    overview: `Every new account starts with a free trial that includes full access. Upgrade any time from the Billing page — no feature is removed, you simply continue without interruption.`,
    benefits: [
      'Full access to every feature during the trial',
      'Upgrade anytime — no data loss, no re-setup',
      'Billing is handled securely by Stripe',
    ],
    steps: [
      {
        title: 'Open Billing',
        desc: 'Click "Billing" in the sidebar to see your current plan, price, and billing cycle.',
        screenshot: 'https://restaff.b-cdn.net/Screenshots/billing-page.jpg',
      },
      {
        title: 'Review your plan',
        desc: 'The Current Plan card shows your tier (Single or Multi Location), price, and next billing date.',
      },
      {
        title: 'Upgrade when ready',
        desc: 'In the Upgrade Plan card, click "Upgrade to Multi" to add unlimited locations. You\'ll be taken to a secure Stripe checkout.',
        tip: 'Have a promo code? Click "Have a promo code?" before checkout.',
      },
      {
        title: 'Manage payment or cancel',
        desc: 'Click "Manage Billing Portal" to update your card, download invoices, or cancel your subscription.',
      },
    ],
  },

  /* ─────────────────────────────────────────────
     NOTIFICATIONS
  ───────────────────────────────────────────── */
  'notifications': {
    title: 'How notifications work in ReStaff',
    description: 'Stay on top of announcements, schedule updates, and important workforce events.',
    icon: '🔔',
    category: 'Core Features',
    screenshot: 'https://restaff.b-cdn.net/Screenshots/notifications-page.jpg',
    sections: [
      { id: 'overview', label: 'Overview' },
      { id: 'benefits', label: 'Key Benefits' },
      { id: 'guide',    label: 'Step-by-Step' },
    ],
    overview: `The Notifications page is your inbox inside ReStaff. Every announcement and workforce alert lands here so nothing gets missed.`,
    benefits: [
      'One place for every announcement and alert',
      'Unread count shown in the sidebar',
      'One-click "Mark All Read" to clear your inbox',
    ],
    steps: [
      {
        title: 'Open Notifications',
        desc: 'Click "Notifications" in the sidebar. Unread items are highlighted.',
        screenshot: 'https://restaff.b-cdn.net/Screenshots/notifications-page.jpg',
      },
      {
        title: 'Read an item',
        desc: 'Click any notification card to view the full message, sender, and time.',
      },
      {
        title: 'Mark items as read',
        desc: 'Click the checkmark on a single item, or click "Mark All Read" at the top to clear everything at once.',
        tip: 'New announcements posted by managers show up here instantly.',
      },
    ],
  },

  /* ─────────────────────────────────────────────
     SENDPULSE SMTP / EMAIL NOTIFICATIONS
  ───────────────────────────────────────────── */
  'resend-smtp': {
    title: 'Email Notifications (Resend)',
    description: 'Set up automatic email delivery for shift assignments, announcements, and payslips using your own Resend account.',
    icon: '📧',
    category: 'Administration',
    demo: null,
    screenshot: 'https://restaff.b-cdn.net/Screenshots/resend.png',
    sections: [
      { id: 'overview',         label: 'Overview' },
      { id: 'benefits',         label: 'Key Benefits' },
      { id: 'when-to-use',      label: 'When to Use' },
      { id: 'setup',            label: 'Setup' },
      { id: 'guide',            label: 'Step-by-Step' },
      { id: 'email-types',      label: 'Email Types' },
      { id: 'tips',             label: 'Tips' },
      { id: 'troubleshooting',  label: 'Troubleshooting' },
      { id: 'faq',              label: 'FAQ' },
    ],
    overview: `Staff Email Notifications lets you send automatic emails to your team using your own Resend account via the Resend REST API. Once configured, ReStaff will automatically notify staff when shifts are assigned, announcements are posted, or payslips are ready — reducing no-shows and improving operational communication.`,
    benefits: [
      'Automatic email delivery — no manual sending required',
      'Reduce no-shows with instant shift notifications',
      'Professional branded emails sent from your own domain',
      'Uses YOUR Resend account — no email limits from ReStaff',
      'REST API only — no SMTP host, port, or password to configure',
      'One-time setup — API key encrypted and saved securely',
    ],
    whenToUse: `Use Staff Email Notifications if you want to automate communication with your team via email. This is especially useful for remote staff, part-time workers, or multi-location teams where in-app notifications may not be sufficient. Emails are sent for shift assignments, schedule updates, announcements, and payslip delivery.`,
    onboarding: `First time setting up email notifications? You'll need a Resend account. Follow the five-step guide below to create your account, verify your sending domain, generate an API key, and plug it into ReStaff in under 5 minutes.`,
    steps: [
      {
        title: 'Step 1 — Create a Resend account',
        desc: 'Visit https://resend.com and sign up. You can sign in with email or Google. Resend is built around a simple REST API, so there are no SMTP credentials to manage.',
        tip: 'Use a company email address (not a personal one) when creating your Resend account.',
      },
      {
        title: 'Step 2 — Verify your sending domain',
        desc: 'In the Resend dashboard, open Domains and add the domain you want to send from (for example, company.com). Resend will give you a small set of DNS records (SPF, DKIM, and an optional return-path). Add them in your DNS provider exactly as shown. Resend will mark the domain as Verified once propagation completes.',
        tip: 'Domain verification protects deliverability. Sending from an unverified domain or from a free provider (gmail.com, yahoo.com) will land in spam.',
      },
      {
        title: 'Step 3 — Generate an API key',
        desc: 'In the Resend dashboard, go to API Keys and click Create API Key. Give it a clear name (for example, "ReStaff production") and Sending access. Copy the full key — it starts with re_ and is shown only once.',
        tip: 'Treat the API key like a password. If it leaks, revoke it from the Resend dashboard and generate a new one.',
      },
      {
        title: 'Step 4 — Add the API key into ReStaff Settings',
        desc: 'In ReStaff, open Settings → Staff Email Notifications. Paste your Resend API key into the Resend API Key field. The key is encrypted before storage and never displayed in full — you will only see a masked preview after saving.',
        tip: 'After saving, the field will show "Configured (re_•••••…xxxx)". Leave it blank on future edits unless you need to rotate the key.',
      },
      {
        title: 'Step 5 — Configure sender email',
        desc: 'Still on Settings → Staff Email Notifications, fill in From Email (for example, hello@company.com) and From Name (your business name). Optionally add a Reply-To email if you want replies to land somewhere other than the From address. Toggle Enable Resend to ON, then click Save configuration.',
        tip: 'From Email MUST be on a domain you verified in Step 2. Resend will reject emails sent from unverified domains.',
      },
      {
        title: 'Send a test email',
        desc: 'After saving, scroll to the Send a test email section, enter your own address, and click Send test. The test is sent directly via the Resend REST API. Check your inbox (and spam folder) — if the email arrives, your setup is complete.',
        tip: 'There is a rate limit of 5 test emails per 10 minutes per tenant to prevent accidental spam. Wait for the cooldown if you hit it.',
      },
    ],
    emailTypes: `Once enabled, ReStaff will automatically send the following types of emails to your staff:

**Shift Assignment Emails**
Sent when a new shift is created and assigned to an employee. Includes shift date, time, location, and position. Staff receive this immediately after the shift is published.

**Announcement Emails**
Sent when a manager posts a company-wide or location-specific announcement. Includes the announcement title and full message body.

**Payslip Ready Emails**
Sent when a payroll run is finalized and payslips are generated. Includes a link to view/download the payslip PDF. Staff receive this once per pay period.

All emails are sent using your Resend account — ReStaff does not impose any additional limit on top of Resend's plan.`,
    tips: [
      'Use a professional "From Email" like no-reply@yourbusiness.com instead of a personal Gmail address.',
      'Verify your sending domain in Resend before going live — unverified domains will be rejected.',
      'Send yourself a test email after every configuration change.',
      'If staff report not receiving emails, ask them to check spam/junk folders first.',
      'Your API key is encrypted at rest. Rotate it from the Resend dashboard if you suspect it has leaked.',
      'Disable the Enable Resend toggle if you ever need to pause outbound emails without losing your configuration.',
    ],
    troubleshooting: [
      {
        problem: 'Test email not received',
        solution: 'Check your spam/junk folder first. If still not found, confirm your Resend API key is valid (Resend dashboard → API Keys) and that the From Email is on a verified domain. Make sure the Enable Resend toggle in ReStaff is ON, then try sending the test again.',
      },
      {
        problem: 'Staff not receiving shift emails',
        solution: 'Verify the employee has a valid email address in Staff Management → Edit Employee → Email. Then check the Resend dashboard → Emails to see if the email reached Resend and what status it ended in (delivered, bounced, etc.).',
      },
      {
        problem: 'Emails going to spam',
        solution: 'Make sure your sending domain in Resend is fully verified (SPF + DKIM both green). Ask staff to mark your emails as "Not Spam" and add your sender address to their contacts. Sending reputation builds over the first few weeks.',
      },
      {
        problem: '"API key is invalid" when sending a test',
        solution: 'The key you pasted is either revoked or has the wrong scope. Generate a new key in Resend → API Keys with Sending access and paste it back into ReStaff. The masked preview will update after save.',
      },
      {
        problem: 'Rate-limit error when sending tests',
        solution: 'ReStaff limits test emails to 5 per 10 minutes per tenant to prevent abuse. Wait until the cooldown expires (the error message includes the seconds remaining), then try again.',
      },
    ],
    faq: [
      { q: 'Do I need a paid Resend account?', a: 'No — Resend has a free tier that is sufficient for most teams. Upgrade to a paid plan from your Resend dashboard if you need higher volume.' },
      { q: 'Can I customize the email templates?', a: 'Not currently. Email templates are pre-designed and automatically populated with data from ReStaff.' },
      { q: 'Will staff replies go to me?', a: 'By default, replies go to the From Email. Set a Reply-To address if you want replies routed elsewhere, or use a no-reply From Email if you want to discourage replies entirely.' },
      { q: 'Can I send emails to specific employees only?', a: 'Emails are sent automatically based on system events (shift creation, payslip generation, etc.). To notify specific staff, use Announcements with targeted locations or roles.' },
      { q: 'What happens if I disable Resend?', a: 'No tenant emails will be sent. Staff will only receive in-app notifications. You can re-enable Resend at any time without losing your configuration.' },
      { q: 'Is my API key stored securely?', a: 'Yes — the key is encrypted at rest and never displayed in full. After save you only see a masked preview such as re_•••••…xxxx.' },
      { q: 'Can I use a different email provider?', a: 'Tenant transactional emails currently use Resend only. Other providers may be added in future updates.' },
    ],
  },

  'sendpulse-smtp': {
    title: 'Email Notifications (SendPulse SMTP)',
    description: 'Set up automatic email delivery for shift assignments, announcements, and payslips using your own SendPulse account.',
    icon: '📧',
    category: 'Administration',
    demo: null,
    screenshot: 'https://restaff.b-cdn.net/Screenshots/sendpulse.png',
    sections: [
      { id: 'overview',         label: 'Overview' },
      { id: 'benefits',         label: 'Key Benefits' },
      { id: 'when-to-use',      label: 'When to Use' },
      { id: 'setup',            label: 'Setup' },
      { id: 'guide',            label: 'Step-by-Step' },
      { id: 'email-types',      label: 'Email Types' },
      { id: 'tips',             label: 'Tips' },
      { id: 'troubleshooting',  label: 'Troubleshooting' },
      { id: 'faq',              label: 'FAQ' },
    ],
    overview: `Staff Email Notifications lets you send automatic emails to your team using your own SendPulse SMTP account. Once configured, ReStaff will automatically notify staff when shifts are assigned, announcements are posted, or payslips are ready — reducing no-shows and improving operational communication.`,
    benefits: [
      'Automatic email delivery — no manual sending required',
      'Reduce no-shows with instant shift notifications',
      'Professional branded emails with your company logo',
      'Uses YOUR SendPulse account — no email limits from ReStaff',
      'Free tier: 12,000 emails/month included with SendPulse',
      'One-time setup — credentials saved permanently',
    ],
    whenToUse: `Use Staff Email Notifications if you want to automate communication with your team via email. This is especially useful for remote staff, part-time workers, or multi-location teams where in-app notifications may not be sufficient. Emails are sent for shift assignments, schedule updates, announcements, and payslip delivery.`,
    onboarding: `First time setting up email notifications? You'll need a free SendPulse account. Follow the step-by-step guide below to create your account, get your API credentials, and configure ReStaff in under 5 minutes.`,
    steps: [
      {
        title: 'Create a SendPulse Account',
        desc: 'Visit https://sendpulse.com/pricing/smtp and sign up for a free account. The free tier includes 12,000 emails per month — more than enough for most small to mid-sized teams. You can upgrade later if needed.',
        tip: 'Use a company email address (not a personal one) when creating your SendPulse account.',
      },
      {
        title: 'Get Your API Credentials',
        desc: 'After signing in to SendPulse, navigate to Settings > API. Enable API access if it\'s not already turned on. You\'ll see two values: API ID and API Secret. Copy both - you\'ll need them in the next step.',
        tip: 'Keep your API Secret secure. Do not share it publicly or commit it to version control.',
      },
      {
        title: 'Configure ReStaff Settings',
        desc: 'In ReStaff, go to Settings > Staff Email Notifications. Paste your API ID and API Secret into the corresponding fields. Fill in the "From Name" (your business name) and "From Email" (e.g., no-reply@yourdomain.com). Optionally, add a Company Logo URL (PNG or JPG, 200-400px width recommended). Toggle "Enable SendPulse" to ON, then click Save Configuration.',
        tip: 'Use a no-reply email address to avoid staff replying to automated emails.',
      },
      {
        title: 'Send a Test Email',
        desc: 'After saving, scroll down to the "Send Test Email" section. Enter your own email address and click Send Test. Check your inbox (and spam folder) to verify the email arrived. If it did, your setup is complete.',
        tip: 'If the test email does not arrive within 2 minutes, check your spam/junk folder first.',
      },
      {
        title: 'Step 1 — Add SPF Record (improves deliverability)',
        desc: 'Without proper authentication, your emails may go to spam or be blocked. Go to your domain provider (GoDaddy, Namecheap, Cloudflare, Hostinger). Find DNS Settings / Zone Editor and add a TXT record. Host: @  Value: v=spf1 include:spf.sendpulse.com ~all  IMPORTANT: If you already have an SPF record, edit it instead of creating a new one. Having multiple SPF records will break email delivery.',
        tip: 'Use the Copy SPF button in Settings → Staff Email Notifications to copy the value exactly.',
      },
      {
        title: 'Step 2 — Add DKIM',
        desc: 'In SendPulse: Settings → SMTP → Domains. Add your domain and copy the DKIM records SendPulse provides. Go back to your DNS provider and add them as TXT records.',
        tip: 'DKIM uses a unique selector (e.g., "sendpulse"). Add the host exactly as SendPulse shows it.',
      },
      {
        title: 'Step 3 — Wait & Verify',
        desc: 'DNS changes can take up to 24 hours to propagate. Once they have, open ReStaff → Settings → Staff Email Notifications and click "Check DNS Status". You should see SPF Detected and DKIM Detected.',
        tip: 'If DKIM stays "missing" after 24 hours, double-check the host name has no extra dots or your domain at the end (some DNS providers append it automatically).',
      },
      {
        title: 'Step 4 — Use a Professional Email',
        desc: 'Use an email like yourname@yourdomain.com. Avoid Gmail, Yahoo, etc. — sending from a free provider on behalf of another domain almost always lands in spam.',
      },
      {
        title: 'Step 5 — Add DMARC (Recommended)',
        desc: 'DMARC tells receivers what to do when SPF/DKIM fail and gives you visibility into delivery. Add a TXT record:  Host: _dmarc  Value: v=DMARC1; p=none; rua=mailto:your@email.com  Replace your@email.com with the inbox where you want delivery reports. Start with p=none to monitor; tighten later to quarantine or reject.',
        tip: 'Use the Copy DMARC button in Settings → Staff Email Notifications to copy the record exactly.',
      },
      {
        title: 'Preview Email Templates',
        desc: 'Below the Test Email section, you will see three tabs: Shift, Announcement, and Payslip. Click each tab to preview the email templates your staff will receive. These templates are automatically populated with real data when emails are sent.',
        tip: 'Email templates are pre-designed and cannot be customized. Your company logo will appear in the header once configured.',
      },
      {
        title: 'Monitor Email Delivery Logs',
        desc: 'At the bottom of the Staff Email Notifications page, you will find the Email Delivery Logs table. This shows the status of all sent emails (pending, sent, failed), recipient addresses, and timestamps. Use this to troubleshoot any delivery issues.',
        tip: 'Failed emails usually indicate an invalid recipient email address. Double-check staff email addresses in Staff Management.',
      },
    ],
    emailTypes: `Once enabled, ReStaff will automatically send the following types of emails to your staff:

**Shift Assignment Emails**
Sent when a new shift is created and assigned to an employee. Includes shift date, time, location, and position. Staff receive this immediately after the shift is published.

**Announcement Emails**
Sent when a manager posts a company-wide or location-specific announcement. Includes the announcement title and full message body.

**Payslip Ready Emails**
Sent when a payroll run is finalized and payslips are generated. Includes a link to view/download the payslip PDF. Staff receive this once per pay period.

All emails are sent using your SendPulse account limits - not ReStaff limits. With the free tier (12,000 emails/month), a team of 50 employees receiving 4 emails each per month would use only 200 emails total.`,
    tips: [
      'Use a professional "From Email" like no-reply@yourbusiness.com instead of a personal Gmail address.',
      'Add your company logo URL - it appears in all email templates and improves brand recognition.',
      'Test email delivery after setup by sending yourself a test email before going live.',
      'Check the Email Delivery Logs weekly to catch any failed deliveries early.',
      'If staff report not receiving emails, ask them to check spam/junk folders first.',
      'Your API credentials are saved permanently - you do not need to re-enter them each time.',
      'Email logs are stored indefinitely - use them for compliance and audit trails.',
    ],
    troubleshooting: [
      {
        problem: 'Test email not received',
        solution: 'Check your spam/junk folder first. If still not found, verify your SendPulse API credentials are correct (Settings → API in SendPulse dashboard). Make sure SendPulse is enabled in ReStaff (toggle should be ON). Try sending the test again.',
      },
      {
        problem: 'Staff not receiving shift emails',
        solution: 'Verify the employee has a valid email address in Staff Management → Edit Employee → Email. Check the Email Delivery Logs at the bottom of Staff Email Notifications to see if the email was sent and its status. If status is "failed", the email address may be invalid.',
      },
      {
        problem: 'Emails going to spam',
        solution: 'This is common with new SendPulse accounts. Ask staff to mark your emails as "Not Spam" and add your sender email to their contacts. Over time, SendPulse will build sender reputation and emails will land in inbox automatically.',
      },
      {
        problem: 'Logo not showing in emails',
        solution: 'Make sure the logo URL is HTTPS (not HTTP) and points directly to an image file (PNG or JPG). Test the URL in a browser to ensure it loads. If it does, re-save the Staff Email Notifications settings to refresh the template.',
      },
      {
        problem: 'API credentials keep disappearing',
        solution: 'They do not disappear — the API Secret field is masked for security. If you see a masked input (••••), your secret is already saved. Leave it blank when saving other fields to avoid overwriting it. If you need to update it, paste a new secret and save.',
      },
      {
        problem: 'SendPulse free tier limit reached',
        solution: 'The free tier includes 12,000 emails/month. If you exceed this, SendPulse will pause email sending. Upgrade to a paid plan in your SendPulse dashboard, or wait until the next month when the limit resets.',
      },
    ],
    faq: [
      { q: 'Do I need a paid SendPulse account?', a: 'No. The free tier includes 12,000 emails/month, which is sufficient for most teams. You can upgrade to a paid plan if needed.' },
      { q: 'Can I customize the email templates?', a: 'Not currently. Email templates are pre-designed and automatically populated with data from ReStaff. Your company logo and branding will appear in the header if configured.' },
      { q: 'Will staff replies go to me?', a: 'No. Use a no-reply email address to prevent staff from replying. Automated emails are one-way notifications, not conversations.' },
      { q: 'Can I send emails to specific employees only?', a: 'Emails are sent automatically based on system events (shift creation, payslip generation, etc.). You cannot manually select recipients. To notify specific staff, use Announcements with targeted locations or roles.' },
      { q: 'What happens if SendPulse is disabled?', a: 'No emails will be sent. Staff will only receive in-app notifications (if they have the mobile app installed). You can re-enable SendPulse at any time without losing your configuration.' },
      { q: 'Are my API credentials stored securely?', a: 'Yes. API credentials are encrypted and stored in your tenant settings. They are never exposed to other users or displayed in plain text (masked as ••••).' },
      { q: 'Can I use a different email provider (not SendPulse)?', a: 'Not currently. ReStaff only supports SendPulse SMTP for transactional emails. Other providers may be added in future updates.' },
    ],
  },

  /* ─────────────────────────────────────────────
     SUPERVISOR KPI & BONUS PAYOUTS
  ───────────────────────────────────────────── */
  'kpi-bonus': {
    title: 'Supervisor KPI & Bonus Payouts',
    description: 'Score every supervisor shift on service performance and automatically pay a $5 bonus when they hit GREEN.',
    icon: '🏆',
    category: 'Payroll',
    screenshot: '/help/kpi-bonus-modal.jpg',
    sections: [
      { id: 'overview',        label: 'Overview' },
      { id: 'benefits',        label: 'Key Benefits' },
      { id: 'when-to-use',     label: 'When to Use' },
      { id: 'guide',           label: 'Step-by-Step' },
      { id: 'scoring',         label: 'How the Score Works' },
      { id: 'tips',            label: 'Tips' },
      { id: 'troubleshooting', label: 'Troubleshooting' },
      { id: 'faq',             label: 'FAQ' },
    ],
    overview: `Supervisor KPI is a per-shift performance score (0–100) that turns daily service quality into a measurable, payable metric. After every shift you record a few quick signals — tables served, 5-star reviews, upsells, complaints, checklists, and the end-of-day report. The system computes a score, colour-codes the shift (GREEN / YELLOW / RED), and when the supervisor scores **85 or higher (GREEN)** the shift becomes "bonus eligible" — a flat **$5 bonus** is automatically added to that shift's payroll. No spreadsheets, no manual bonus tracking, no override.`,
    benefits: [
      'Tie pay directly to service quality — supervisors who deliver get paid more',
      'Per-shift granularity — bonuses earned on the day, not at vague month-end reviews',
      'Strictly deterministic: 85+ score = $5 bonus, every time. No favouritism, no override',
      'Already wired into payroll, CSV, Excel, and the Labor Dashboard — zero extra setup',
      'Live preview while you type — score updates before you save',
      'Visual badges on the timesheet make it obvious which shifts earned bonus',
    ],
    whenToUse: `Use Supervisor KPI on every supervisor or manager shift you want to incentivize. After the shift ends and the supervisor clocks out, the manager (or supervisor themselves, if permitted) opens the timesheet entry and fills the Supervisor KPI section. Best practice: do this within 24 hours of the shift while the numbers are fresh.`,
    onboarding: `KPI is optional and works on any clocked-out shift. You don't need to enable anything — just open a completed shift and start filling the Supervisor KPI section.`,
    steps: [
      {
        title: 'Open a completed shift',
        desc: 'Go to Timesheets in the sidebar (or visit /timesheets). Find a clocked-out shift and click the pencil edit icon at the far right of the row. The "Edit Time Entry" modal opens.',
        tip: 'Only shifts with both a clock-in and a clock-out can earn KPI. Active shifts (still clocked in) and rejected shifts are excluded.',
      },
      {
        title: 'Fill the Supervisor KPI section',
        desc: 'Scroll down to the "Supervisor KPI — optional" section. Fill in the four numeric fields (Tables served, 5★ reviews, Upsell tables, Complaints) and tick the four checkboxes (Opening / Mid-shift / Closing checklists, Report submitted). As you type, a live preview shows the score and status.',
        screenshot: '/help/kpi-bonus-modal.jpg',
        tip: 'Use 0 for Complaints if there were none — leaving it blank is treated as no data and may suppress the score.',
      },
      {
        title: 'Save the KPI',
        desc: 'Click "Save KPI" inside the KPI section (separate from the main "Save Changes" button). The shift instantly displays the score badge in the timesheet table and, if GREEN, a violet "+ $5 bonus" badge.',
        screenshot: '/help/kpi-bonus-badges.jpg',
        tip: 'If the shift was already Approved, saving KPI resets it to Pending Approval — a manager will need to re-approve before payroll picks it up.',
      },
      {
        title: 'See the bonus in Payroll Summary',
        desc: 'Switch to the Payroll Summary tab on the Timesheets page. A new "Bonus" column appears with the $5 per qualifying shift, plus quick "Total Tips" and "Total Bonuses" lines above the table. The Total Pay column already includes the bonus.',
        screenshot: '/help/kpi-bonus-payroll.jpg',
        tip: 'The Bonus column only appears when at least one employee earned a bonus in the period — otherwise it stays hidden to keep the view clean.',
      },
      {
        title: 'See the period total on the Labor Dashboard',
        desc: 'Open Dashboard → Labor tab. The "Bonuses Paid" violet KPI card shows the total bonus paid out for the selected date range, alongside Tips, Overtime, and Missed Breaks.',
      },
      {
        title: 'Export everything to CSV',
        desc: 'On the Payroll Summary tab click "Export Payroll" → CSV. The CSV includes Bonus Shifts and Bonus ($) columns per employee, plus a footer summary with "Total Tips" and "Total Bonuses" — accountant-ready.',
      },
    ],
    scoring: {
      intro: 'Score formula (out of 100 points):',
      rows: [
        { metric: '5★ review rate',      weight: '25 pts', rule: 'Reviews ≥ 50% of tables → full points (partial credit applies)' },
        { metric: 'Upsell rate',         weight: '25 pts', rule: 'Upsells ≥ 25% of tables → full points (partial credit applies)' },
        { metric: 'Zero complaints',     weight: '20 pts', rule: 'Complaints = 0 (any complaint → 0 pts)' },
        { metric: 'All 3 checklists',    weight: '15 pts', rule: 'Opening + mid-shift + closing all ticked' },
        { metric: 'Report submitted',    weight: '15 pts', rule: 'End-of-day report ticked' },
      ],
      thresholds: [
        { dot: 'green',  label: 'GREEN  ·  ≥85',     desc: 'Bonus eligible — automatic +$5 added to total pay' },
        { dot: 'yellow', label: 'YELLOW · 70–84',    desc: 'Solid shift, no bonus' },
        { dot: 'red',    label: 'RED    · <70',      desc: 'Flagged for review (visible in Labor Dashboard "Failed Shifts")' },
      ],
      footer: 'Reviews and upsells use partial credit: e.g. 30% review rate earns 25 × (0.30 / 0.50) = 15 pts.',
    },
    tips: [
      'Record KPI within 24 hours of the shift while the numbers are fresh — managers tend to forget by week-end.',
      'Tie this metric to your supervisor onboarding: explain GREEN = $5 extra per shift × ~20 shifts/month = $100/month upside.',
      'Use the "Avg KPI Score" card on the Labor Dashboard to spot trends — a dropping average usually points to weakening checklist discipline.',
      'Bonus payouts are visible in the CSV export footer (Total Bonuses) — give a copy to your accountant for the period close.',
      'KPI data is immutable in finalized payroll runs — once a payroll is finalized, the bonus locks in even if you edit the KPI later.',
    ],
    troubleshooting: [
      {
        problem: 'KPI score does not show after saving',
        solution: 'Refresh the timesheets page. If the score still does not appear, ensure all four numeric fields have a value (use 0, not blank, for "no complaints"). The score is suppressed when no KPI signal is recorded at all.',
      },
      {
        problem: 'GREEN score but no bonus on Payroll Summary',
        solution: 'Check that the shift is approved — only approved, completed shifts roll into payroll. Saving KPI on an approved shift resets it to Pending Approval; a manager must re-approve.',
      },
      {
        problem: 'Bonus column not visible in Payroll Summary',
        solution: 'The Bonus column is hidden when zero bonuses have been earned in the selected period. Either widen the date range or save KPI on at least one GREEN shift to see the column appear.',
      },
      {
        problem: 'Cannot save KPI — "Save KPI" button is greyed out',
        solution: 'You need at least one KPI field filled in. The button activates only when the system detects meaningful data (any number entered or any checkbox ticked).',
      },
      {
        problem: 'I want to change the bonus amount from $5',
        solution: 'The bonus is intentionally fixed at $5 per shift to keep payouts predictable and tamper-proof. If you need a different amount, contact support — it is a one-line constant change, not a per-tenant setting.',
      },
    ],
    faq: [
      { q: 'Who can record KPI?', a: 'Owners, Managers, and Super Admins can record and edit KPI on any shift in their tenant. Staff cannot edit KPI on their own shifts.' },
      { q: 'Can a supervisor earn a bonus on their own shift?', a: 'Yes — that is exactly the point. The bonus accrues to the employee whose shift it is, regardless of who entered the KPI numbers. Best practice: a manager validates the numbers to keep things honest.' },
      { q: 'Is the bonus pre-tax or post-tax?', a: 'The $5 is added to gross pay (total_pay) before any tax or deduction handling. Your local tax rules apply as normal.' },
      { q: 'Can I override and pay $10 instead of $5 for an outstanding shift?', a: 'No. The bonus is strictly deterministic: GREEN = $5, no override. This prevents favouritism and makes payroll predictable. To reward a single exceptional shift differently, use the Cash Advances or Pay Rules feature instead.' },
      { q: 'What if a supervisor scored YELLOW but really deserves a bonus?', a: 'KPI ≥85 is the threshold. If the supervisor consistently lands in YELLOW, the score formula is telling you something — usually one missed checklist or a single complaint pulled them under. Coach the metric, do not override the formula.' },
      { q: 'Does the bonus show on the employee\'s payslip PDF?', a: 'Yes. Once the payroll run is finalized, the $5 bonus appears in the gross pay total of every payslip the employee earned it on, and the period summary shows the aggregate.' },
      { q: 'Can I disable KPI for non-supervisor staff?', a: 'KPI is opt-in per shift — you simply don\'t fill it in for non-supervisor staff. There is no enable/disable toggle; unused fields stay blank and the score is never computed.' },
      { q: 'Where do I see the total bonuses paid this period?', a: 'Three places: (1) Payroll Summary header — "Total Bonuses: $X"; (2) Labor Dashboard "Bonuses Paid" KPI card; (3) Payroll CSV export footer.' },
    ],
  },
};

/* ────────────────────────────────────────────────────────────────────────
   APPENDED — New feature articles covering shipped capabilities since v3.
   Reuses existing article structure (title/desc/icon/category/screenshot/
   sections + overview/benefits/whenToUse/steps/tips/troubleshooting/faq).
   No backend, routing, or schema changes.
   ──────────────────────────────────────────────────────────────────────── */

ARTICLES['lifetime-marketplace'] = {
  title: 'Lifetime Marketplace (Admin)',
  description: 'Generate and manage AppSumo / DealMirror lifetime deal codes — bulk operations, exports, and disable/restore safety.',
  icon: '🎟️',
  category: 'Administration',
  screenshot: '/help/lifetime-marketplace.jpg',
  sections: [
    { id: 'overview', label: 'Overview' },
    { id: 'benefits', label: 'Key Benefits' },
    { id: 'guide', label: 'Step-by-Step' },
    { id: 'tips', label: 'Tips' },
    { id: 'troubleshooting', label: 'Troubleshooting' },
  ],
  overview: `The Lifetime Marketplace is the Super Admin home for every lifetime-deal code you ship to AppSumo, DealMirror, or any other marketplace. From here you generate code batches, export them as CSV or TXT, edit metadata, disable bad codes, and bulk-process anything in one click. Redeemed codes are protected from deletion to preserve your audit trail.`,
  benefits: [
    'Generate dozens of codes at once and download as CSV or TXT',
    'Bulk disable / enable / delete unused codes from one toolbar',
    'Redeemed codes are protected automatically — they can never be deleted',
    'Per-code Edit lets you change tier or marketplace if you ship to the wrong batch',
    'Filter by status, tier, and marketplace to triage thousands of codes quickly',
  ],
  steps: [
    {
      title: 'Open the Lifetime tab',
      desc: 'Sign in as Super Admin → Super Admin → Lifetime. The Codes panel shows every code in your account with status badges (unused / redeemed / disabled / refunded).',
      tip: 'Use the Status and Marketplace filters at the top to narrow the list before exporting.',
    },
    {
      title: 'Generate a batch',
      desc: 'Click Generate Codes. Pick a marketplace source, tier (Tier 1 / Tier 2 / Unlimited), and quantity. Confirm — the modal then shows all the new codes and three buttons: Close, TXT, Download CSV, Copy to clipboard.',
      tip: 'TXT is one-code-per-line — perfect for pasting into a marketplace upload form. CSV adds marketplace, tier, status, and created date for your records.',
      screenshot: '/help/lifetime-generate-modal.jpg',
    },
    {
      title: 'Run bulk actions',
      desc: 'Tick the header checkbox to select all visible codes or tick individual rows. The orange bulk toolbar appears: Disable Selected, Enable Selected, Delete Selected, Export CSV, Export TXT.',
      tip: 'Redeemed codes are skipped automatically — the summary toast reads e.g. "Disabled 6 · skipped 6 redeemed".',
      screenshot: '/help/lifetime-bulk-toolbar.jpg',
    },
    {
      title: 'Edit, disable, or delete a single code',
      desc: 'Each row has three icon buttons: Edit (pencil), Disable/Enable (power), Delete (red trash). Edit opens a dialog where you can change Marketplace, Tier, and Notes. Disable prevents the code from being redeemed but keeps it visible. Delete is hard-delete (refused on redeemed codes).',
      tip: 'Disable is the safe choice for fraud or marketplace refunds — it preserves the redemption history if the code was already used.',
    },
  ],
  tips: [
    'Generate codes in small batches per marketplace so the CSV is easy to upload — most marketplaces cap uploads at 100–500 codes per file.',
    'Use the Marketplace filter to export only AppSumo codes (for example) and keep DealMirror separate.',
    'Customers and Redemptions sub-tabs are read-only audit views — for refund/disable use the Customers tab, not Codes.',
    'The Notes field on each code is searchable later — use it to record batch IDs or campaign references.',
  ],
  troubleshooting: [
    { problem: 'Generated codes don\'t appear in the table', solution: 'Click Refresh. If the filter dropdowns are set (Status / Marketplace / Tier), clear them — newly generated codes default to status=unused and the current marketplace source.' },
    { problem: 'Delete button is greyed out', solution: 'The code has been redeemed. Redeemed codes are protected — use Disable instead, which prevents future redemptions while keeping the audit trail.' },
    { problem: 'Bulk operation shows "skipped N redeemed"', solution: 'That is expected and safe. Bulk Disable / Delete never affects redeemed codes — only unused/disabled ones. The summary tells you exactly how many were protected.' },
  ],
  faq: [
    { q: 'Can I delete a redeemed code if the customer refunded?', a: 'No — codes themselves stay forever. Instead, go to Customers → find the tenant → Disable. The code is automatically marked as refunded.' },
    { q: 'What is the maximum batch size for Generate?', a: 'There is no hard cap, but for performance generate batches of 100–500 at a time and download each batch separately.' },
  ],
};

ARTICLES['lifetime-activation'] = {
  title: 'Lifetime License Activation',
  description: 'Step-by-step guide for marketplace customers redeeming their lifetime code — including email verification and the resend flow.',
  icon: '✨',
  category: 'Getting Started',
  screenshot: '/help/lifetime-activation.jpg',
  sections: [
    { id: 'overview', label: 'Overview' },
    { id: 'guide', label: 'Step-by-Step' },
    { id: 'tips', label: 'Tips' },
    { id: 'troubleshooting', label: 'Troubleshooting' },
    { id: 'faq', label: 'FAQ' },
  ],
  overview: `Customers who buy a ReStaff lifetime deal on AppSumo, DealMirror, or any partner marketplace activate it at /lifetime. After redeeming the code they receive a verification email — clicking the link activates the lifetime license and unlocks login.`,
  steps: [
    {
      title: 'Open the activation page',
      desc: 'Go to https://getrestaff.co/lifetime — there is no public marketing link, this is the dedicated redemption URL given to marketplace buyers.',
      tip: 'Bookmark this URL; it is the single source of truth for redemption.',
    },
    {
      title: 'Enter your code and account details',
      desc: 'Paste the code from your marketplace receipt (e.g. RS-XXX-T1-XXXX), then fill business name, your name, email, and password.',
    },
    {
      title: 'Check your inbox for the verification email',
      desc: 'After submitting, the activation page shows the "Check your inbox" view. ReStaff sends a verification link from no-reply@getrestaff.co — open it within 24 hours.',
      tip: 'If you don\'t see the email in 2 minutes, check spam and use the "Resend verification email" button (60-second cooldown, up to 5/day).',
    },
    {
      title: 'Click the verification link',
      desc: 'The link opens a "Verifying your email…" page that talks to the ReStaff server and flips your account to active. Once you see "Email verified" you can sign in.',
    },
    {
      title: 'Sign in',
      desc: 'Click "Continue to sign in" or go to /login. Use the email + password you set during redemption.',
    },
  ],
  tips: [
    'Lifetime licenses are bound to the email you redeem with — choose carefully.',
    'You only need to verify once. After that, the account works like any other ReStaff account.',
    'Tier 1 = 1 location, Tier 2 = 5 locations, Unlimited = no cap. Your tier was set when the code was generated.',
  ],
  troubleshooting: [
    { problem: 'Login fails with "Please verify your email to activate your Lifetime license"', solution: 'You haven\'t clicked the verification email yet. Open the latest email titled "Verify your ReStaff email address" and click the link. If it expired, request a new one from the pending screen at /lifetime.' },
    { problem: 'Verification link says "Invalid or already used"', solution: 'You\'ve already verified — try signing in directly at /login.' },
    { problem: 'Resend button is disabled', solution: 'Cooldown: max 1 resend per 60 seconds, max 5 per 24 hours. The button label shows the remaining seconds.' },
    { problem: 'Code shows as "already redeemed"', solution: 'The code was used previously. Marketplace deals are single-use — contact support if you believe this is an error.' },
  ],
  faq: [
    { q: 'How long do I have to verify?', a: '24 hours from redemption. After that the code is automatically released back to inventory and you\'ll need a fresh one.' },
    { q: 'Can I change the email after redemption?', a: 'No — the license is bound to the redeeming email. Contact support if you redeemed with the wrong account.' },
    { q: 'I deleted the verification email by accident.', a: 'Go back to /lifetime, scroll down to the pending screen, and use "Resend verification email".' },
  ],
};

ARTICLES['ai-workforce-insights'] = {
  title: 'AI Workforce Insights & EVA Recommendations',
  description: 'Deterministic recommendations ranked by estimated savings — overtime risk, idle time, workload imbalance, and budget alerts.',
  icon: '🤖',
  category: 'AI Assistant',
  screenshot: '/help/eva-insights.jpg',
  sections: [
    { id: 'overview', label: 'Overview' },
    { id: 'benefits', label: 'Key Benefits' },
    { id: 'guide', label: 'Step-by-Step' },
    { id: 'tips', label: 'Tips' },
    { id: 'faq', label: 'FAQ' },
  ],
  overview: `EVA produces ranked, dollar-quantified recommendations for the current pay period: who is on track to hit overtime, which shifts are at risk, which employees have idle time, and where workload is unevenly distributed. Every insight links to the underlying employee, shift, or report so you can act in two clicks.`,
  benefits: [
    'Every recommendation has an estimated $ impact — prioritise by savings, not guesswork',
    'Detects overtime risk 3–5 days before payroll closes',
    'Idle Time Detection flags clocked-in employees with no activity for >30 min',
    'Workload Balancing highlights employees scheduled >120% of their target hours',
    'No AI API key required — runs on deterministic rules so output is reproducible',
  ],
  steps: [
    { title: 'Open Eva Insights', desc: 'Sidebar → Eva Insights (or /insights). The list is sorted by estimated savings — biggest impact first.' },
    { title: 'Review the top 3', desc: 'Each card shows: the problem (e.g. "Maria is on track for 6h overtime"), the recommendation (e.g. "Cut Saturday shift to 4h"), and the estimated $ saved.' },
    { title: 'Click into the source', desc: 'Each insight links to the underlying employee profile, schedule, or labor report so you can act immediately.' },
    { title: 'Dismiss or snooze', desc: 'If an insight no longer applies, dismiss it. It won\'t reappear in the same form for 7 days.' },
  ],
  tips: [
    'Run a fresh insights check every Monday morning before publishing the schedule.',
    'The $ impact assumes your current overtime multiplier (set in Settings → Payroll). Update it for accurate forecasts.',
    'Insights are tenant-scoped — each location runs its own analysis.',
  ],
  faq: [
    { q: 'Is this powered by GPT?', a: 'No. EVA Insights are deterministic rule-based recommendations — output is reproducible and explainable.' },
    { q: 'How often does it run?', a: 'On every page load of /insights, plus once nightly to feed the Smart Digest email.' },
  ],
};

ARTICLES['smart-digests'] = {
  title: 'Smart Digests, Shift Risk Detection & Automation Recipes',
  description: 'Daily and weekly digest emails surface the highest-impact risks; automation recipes act on them without you lifting a finger.',
  icon: '📬',
  category: 'AI Assistant',
  screenshot: null,
  sections: [
    { id: 'overview', label: 'Overview' },
    { id: 'guide', label: 'Step-by-Step' },
    { id: 'tips', label: 'Tips' },
    { id: 'faq', label: 'FAQ' },
  ],
  overview: `Smart Digests bundle the morning's most important events into one email so you don't open the app blind. Shift Risk Detection flags shifts likely to be missed (no clock-in 10 minutes after start). Automation Recipes chain triggers to actions — e.g. "if a shift goes 30 min unstaffed, auto-post a replacement request to eligible staff".`,
  steps: [
    { title: 'Subscribe to digests', desc: 'Settings → Notifications → Daily Digest / Weekly Digest. Choose the time and the locations to include.' },
    { title: 'Configure shift-risk alerts', desc: 'Settings → Alerts → Missed Clock-in. Default grace is 10 minutes — increase for kitchens that prefer text reminders.' },
    { title: 'Create an Automation Recipe', desc: 'Settings → Automation → New Recipe → pick a trigger (e.g. "Shift unstaffed 30 min after start") and an action ("Post replacement request to eligible employees").' },
    { title: 'Review recipe runs', desc: 'Settings → Automation → History. Every recipe execution is logged with timestamp and outcome.' },
  ],
  tips: [
    'Start with one recipe — most teams enable the missed-clock-in replacement recipe first.',
    'Digests send at the time set in your account timezone — double-check Settings → Locations if your morning email is arriving late.',
  ],
  faq: [
    { q: 'Can recipes auto-approve leave?', a: 'No — leave approval always requires a human. Recipes can notify, escalate, or route, but never finalize personnel decisions.' },
  ],
};

ARTICLES['payroll-summary-estimated'] = {
  title: 'Payroll Summary & Estimated Pay Preview',
  description: 'See projected pay before the period closes — Estimated Pay Preview and the one-page Payroll Summary report.',
  icon: '💵',
  category: 'Payroll',
  screenshot: '/help/run-payroll-dialog.jpg',
  sections: [
    { id: 'overview', label: 'Overview' },
    { id: 'guide', label: 'Step-by-Step' },
    { id: 'tips', label: 'Tips' },
    { id: 'troubleshooting', label: 'Troubleshooting' },
  ],
  overview: `Estimated Pay Preview shows projected gross pay per employee based on approved timesheets to-date — so there are no surprises at payroll close. The Payroll Summary PDF is the one-page report you hand off to your accountant or executive team after running payroll.`,
  steps: [
    { title: 'Open Timesheets', desc: 'Sidebar → Timesheets → select the current pay period.' },
    { title: 'Toggle Estimated Pay Preview', desc: 'In the header, click "Estimated Pay". The table now adds an "Est. Gross" column per employee based on approved hours + overtime + holiday multipliers.' },
    { title: 'Run Payroll', desc: 'When the period closes: Payroll → Run Payroll → review the dialog → finalize. Payslips are emailed automatically.' },
    { title: 'Download the Summary PDF', desc: 'After finalization, click "Payroll Summary PDF" — one page with totals, per-employee breakdown, bonuses, and labor % of revenue.' },
  ],
  tips: [
    'Preview daily during the last week of the pay period so overtime surprises are caught early.',
    'The PDF Summary is also emailed to the owner address — search "ReStaff Payroll Summary" in your inbox.',
  ],
  troubleshooting: [
    { problem: 'Estimated Pay column is empty', solution: 'No approved timesheets yet for that employee. Approve a few entries and refresh.' },
  ],
};

ARTICLES['labor-productivity'] = {
  title: 'Labor vs Productivity, RPLH & Labor Budget Target',
  description: 'Compare labor cost against revenue and productivity in real time — Revenue Per Labor Hour, Labor Budget Target, and Workforce Analytics.',
  icon: '📊',
  category: 'Analytics',
  screenshot: '/help/labor-dashboard.jpg',
  sections: [
    { id: 'overview', label: 'Overview' },
    { id: 'benefits', label: 'Key Benefits' },
    { id: 'guide', label: 'Step-by-Step' },
    { id: 'tips', label: 'Tips' },
  ],
  overview: `The Labor Dashboard now answers four questions in one view: (1) Are we hitting our budget? (2) Are we productive per hour worked? (3) Where is idle time hiding? (4) Is workload balanced across the team?`,
  benefits: [
    'Revenue Per Labor Hour (RPLH) — instantly see if added hours generated proportional revenue',
    'Labor Budget Target — set a weekly/monthly cap; the dashboard turns red when you cross it',
    'Workforce Analytics — heatmaps of who works the most, who clocks late, who skips breaks',
    'Idle Time Detection — clocked-in employees with no completed tasks/orders for >30 min',
    'Workload Balancing — find employees scheduled >120% of their target hours',
  ],
  steps: [
    { title: 'Open Labor Dashboard', desc: 'Sidebar → Labor (or /labor). The header shows current period total + RPLH + budget delta.' },
    { title: 'Set your Labor Budget Target', desc: 'Click the budget pill in the header → enter weekly target (e.g. $4,500). The chart now overlays target vs actual.' },
    { title: 'Drill into a day', desc: 'Click any bar — modal shows per-employee hours, cost, and the RPLH calculation.' },
    { title: 'Export', desc: 'Top-right → Export CSV — full breakdown for your accountant.' },
  ],
  tips: [
    'RPLH = (Revenue logged in the period) ÷ (Total labor hours). Make sure you enter revenue daily in Settings → Revenue.',
    'Set the budget target seasonally — a December target won\'t make sense in February.',
    'Workload Balancing flags employees at >120% of target. Lower the threshold in Settings → Labor → Target Bands if you run a leaner team.',
  ],
};

ARTICLES['compliance-tracking'] = {
  title: 'Compliance Tracking, Break Compliance & GPS-ready Clock Logic',
  description: 'Audit-grade tracking for breaks, location-aware clock-in, and rule violations across every location.',
  icon: '🛡️',
  category: 'Administration',
  screenshot: '/help/timeclock.jpg',
  sections: [
    { id: 'overview', label: 'Overview' },
    { id: 'guide', label: 'Step-by-Step' },
    { id: 'tips', label: 'Tips' },
  ],
  overview: `ReStaff tracks every clock event, break, and missed punch against your configured rules. Break Compliance flags shifts that exceeded the legal threshold without a recorded break. GPS-ready Clock Logic enables (optional) geo-fenced clock-in for businesses that require it — the field stays off by default to protect privacy.`,
  steps: [
    { title: 'Configure break rules', desc: 'Settings → Compliance → Break Rules. Default: 30-min unpaid break required if shift > 6 hours.' },
    { title: 'Enable GPS clock-in (optional)', desc: 'Settings → Time Clock → GPS Mode = On. Set the location radius (default 100m). Employees see a "Allow location" prompt on the clock-in screen.' },
    { title: 'Review violations', desc: 'Reports → Compliance → see every shift that missed a required break or clocked in outside the GPS radius.' },
  ],
  tips: [
    'GPS clock-in is OFF by default. Only enable it if your industry requires location proof.',
    'Break Compliance reports are pre-formatted for labor-board audits — export CSV directly.',
  ],
};

ARTICLES['leave-management'] = {
  title: 'Leave Management',
  description: 'Track vacation, sick days, and unpaid leave with approval queue, calendar view, and CSV import for legacy data.',
  icon: '🌴',
  category: 'Workforce Management',
  screenshot: '/help/leave-management.jpg',
  sections: [
    { id: 'overview', label: 'Overview' },
    { id: 'benefits', label: 'Key Benefits' },
    { id: 'guide', label: 'Step-by-Step' },
    { id: 'tips', label: 'Tips' },
  ],
  overview: `Leave Management replaces the old shared spreadsheet. Employees request leave from the mobile app, managers approve from the Approval Queue, and approved leave shows up on the schedule automatically as blocked time so nobody is scheduled over it.`,
  benefits: [
    'Three tabs: My Requests, Approval Queue, All Requests — one for every role',
    'Calendar view shows the whole team\'s approved leave at a glance',
    'Approved leave automatically blocks schedule slots so no double-booking',
    'CSV Import migrates legacy leave data from your old system',
    'Leave Types (Vacation, Sick, Unpaid, Annual) are fully editable per business',
  ],
  steps: [
    { title: 'Open Leave', desc: 'Sidebar → Leave. The default view is "My Requests" for individual users, or "Approval Queue" for managers.' },
    { title: 'New Request', desc: 'Click New Request → pick type, dates, optional note. Approval routes to your reporting manager.' },
    { title: 'Approve from Queue', desc: 'Approval Queue tab → click Approve / Reject on each pending row. Comment is required on rejection.' },
    { title: 'Configure Leave Types', desc: 'Click "Leave Types" in the header → add custom types like "Bereavement" or "Jury Duty" with their own color.' },
    { title: 'Import legacy data', desc: 'Click Import → upload CSV with columns: employee_email, type, start_date, end_date, status.' },
  ],
  tips: [
    'Cancel an approved leave from the row\'s Cancel button — schedule slots unblock immediately.',
    'Calendar tab is great for spotting clustering — e.g. half the team requesting the same week.',
  ],
};

ARTICLES['shift-replacement'] = {
  title: 'Shift Replacement Workflow & Coverage Health',
  description: 'Employees request replacements; eligible coworkers accept; managers see Coverage Health turn green.',
  icon: '🔄',
  category: 'Workforce Management',
  screenshot: null,
  sections: [
    { id: 'overview', label: 'Overview' },
    { id: 'benefits', label: 'Key Benefits' },
    { id: 'guide', label: 'Step-by-Step' },
    { id: 'tips', label: 'Tips' },
  ],
  overview: `Shift Replacement is the formal version of "can someone cover my shift?". Employees post the request, eligible coworkers (right role + available + at the same location) see it and accept, managers confirm. Coverage Health is the dashboard widget that shows you live coverage status across every shift today and tomorrow.`,
  benefits: [
    'Replacement requests are restricted to eligible coworkers automatically — no manual matching',
    'Coverage Health turns red/yellow/green so you know in 2 seconds whether tonight is covered',
    'Push + email notifications fire to every eligible candidate the moment a request opens',
    'Manager approval is required before the schedule changes — no surprise swaps',
  ],
  steps: [
    { title: 'Employee requests replacement', desc: 'Mobile app → My Shifts → Request Replacement → optional reason. Notification fans out to eligible coworkers.' },
    { title: 'Coworker accepts', desc: 'Eligible employees see the request in their notifications and tap Accept. The request enters Manager Review.' },
    { title: 'Manager approves', desc: 'Dashboard → Replacements (or /replacements). Click Approve — the schedule updates and both employees are notified.' },
    { title: 'Check Coverage Health', desc: 'Dashboard → Coverage Health card. Green = covered, yellow = pending replacement, red = uncovered.' },
  ],
  tips: [
    'Set the maximum hours-before-shift you allow replacement requests in Settings → Schedule → Replacement Window (default 48h).',
    'Replacement notifications respect quiet hours — same as regular shift notifications.',
  ],
};

ARTICLES['approval-pipelines'] = {
  title: 'Approval Pipelines',
  description: 'Multi-step approval routing for leave, replacements, timesheet edits, and payroll runs.',
  icon: '✅',
  category: 'Administration',
  screenshot: null,
  sections: [
    { id: 'overview', label: 'Overview' },
    { id: 'guide', label: 'Step-by-Step' },
    { id: 'tips', label: 'Tips' },
  ],
  overview: `Approval Pipelines let larger businesses route requests through more than one approver — e.g. leave needs your direct manager AND the store GM. Each pipeline has steps, each step has approvers, and the request advances only after every step approves.`,
  steps: [
    { title: 'Create a pipeline', desc: 'Settings → Approvals → New Pipeline. Name it (e.g. "Vacation > 5 days") and pick the trigger event.' },
    { title: 'Add steps', desc: 'Each step has one or more approvers and a condition. Step 1 might be "direct manager", step 2 "store GM".' },
    { title: 'Set bypass rules', desc: 'Optional — auto-approve if the request is < 24h or from a senior employee. Bypass rules apply per step.' },
    { title: 'Watch requests flow through', desc: 'Approvals tab shows the live state of every in-flight request. Bottlenecks are flagged in orange.' },
  ],
  tips: [
    'Keep pipelines short — most teams need 1 or 2 steps. More than 3 creates friction.',
    'Approvers are notified by email AND in-app. Pending requests appear at the top of the Approval Queue.',
  ],
};

ARTICLES['ai-integrations'] = {
  title: 'AI Integrations (ChatGPT, Claude, Gemini via MCP)',
  description: 'Connect your ReStaff tenant to ChatGPT, Claude, or Gemini securely through the Model Context Protocol — read-only, scoped, revocable in one click.',
  icon: '🤖',
  category: 'AI Assistant',
  screenshot: '/help/ai-connections.jpg',
  sections: [
    { id: 'overview', label: 'Overview' },
    { id: 'providers', label: 'Supported Providers' },
    { id: 'capabilities', label: 'Capabilities' },
    { id: 'permissions', label: 'Permissions Model' },
    { id: 'security', label: 'Security Model' },
    { id: 'rate-limits', label: 'Rate Limits' },
    { id: 'connect', label: 'How to Connect' },
    { id: 'disconnect', label: 'How to Disconnect' },
    { id: 'troubleshooting', label: 'Troubleshooting' },
  ],
  overview: `ReStaff implements the Model Context Protocol (MCP), an open standard that lets AI assistants safely call your operational data — read-only — through OAuth-authorized tool calls. Owners decide who can connect, which providers are allowed, and which scopes they receive. Every call is audited.`,
  providers: [
    'ChatGPT (OpenAI) — connect from chatgpt.com → Settings → Connectors.',
    'Claude (Anthropic) — connect from claude.ai → Settings → Connectors.',
    'Gemini (Google) — connect from gemini.google.com → Extensions → Add custom MCP.',
    'Server endpoint to share with your provider: https://api.getrestaff.co/api/mcp',
  ],
  capabilities: [
    'get_staff — list active employees (no passwords, no PINs)',
    'get_schedule — published & draft shifts filterable by date / location',
    'get_open_shifts — currently unassigned shifts awaiting fill',
    'get_timesheet — completed clock-in/out entries with hours summary',
    'get_leave_balance — annual / used / remaining days per employee',
    'get_labor_cost — aggregated cost & hours by location and date',
    'All tools are READ-ONLY in V1. AI cannot edit schedules, run payroll, modify staff, or delete data.',
  ],
  permissions: [
    'Default: ONLY the tenant owner can connect AI providers. Managers and staff are blocked until the owner opts them in.',
    'Toggle "Allow Manager MCP access" / "Allow Staff MCP access" in Settings → AI Connections.',
    'Scopes granted at connect time: staff.read, schedule.read, timesheet.read, leave.read, analytics.read. Each scope is independent — connect with only the ones you want to share.',
    'Permission changes take effect immediately. Disabling Manager access revokes any active token a manager already held.',
  ],
  security: [
    'OAuth 2.1 + PKCE — public clients (chatgpt/claude/gemini) use S256 challenges, no client secrets stored.',
    'Tokens are short-lived JWTs (1-hour TTL) bound to a specific resource URL with audience + issuer claims.',
    'Every MCP tool query is tenant-scoped server-side: { restaurant_group_id: current_user.restaurant_group_id }. AI from tenant A can never see tenant B data.',
    'Disconnect a provider and ALL of its active tokens are revoked within a few milliseconds — no waiting for token expiry.',
    'Failed-auth spikes, scope violations, and rate-limit abuse are auto-detected and surfaced as Security Alerts on the Super Admin dashboard.',
    'ReStaff operations can pause individual providers (e.g., during a Claude outage) or disable MCP globally with an Emergency Kill Switch.',
    '180-day audit retention. Export to CSV from Settings → AI Connections → Export CSV.',
  ],
  rateLimits: [
    'Owner role: 500 MCP requests per hour (per user).',
    'Manager role: 250 MCP requests per hour.',
    'Staff role: 50 MCP requests per hour.',
    'Tenant fair-use quota: 50,000 requests / calendar month by default. Soft alerts at 80% / 95% / 100% — no hard billing lock.',
    'Auto-pause via Circuit Breaker: 50+ failures from a single provider in 5 minutes trips the breaker for 5 minutes.',
    'When a limit trips you receive HTTP 429 with Retry-After header. AI clients retry automatically.',
  ],
  connect: [
    'Go to Settings → AI Connections (this page).',
    'Click "Connect ChatGPT" / "Connect Claude" / "Connect Gemini" — the wizard shows the exact steps for that provider.',
    'In your AI provider, add a new MCP connector pointing at https://api.getrestaff.co/api/mcp.',
    'You will be redirected to ReStaff to sign in with your tenant credentials and approve the requested scopes.',
    'On success, ReStaff issues a 1-hour access token to the provider and starts an active session.',
    'Status returns to Settings → AI Connections with a green "Connected" badge plus the scopes granted.',
  ],
  disconnect: [
    'Settings → AI Connections → row for the provider → click Disconnect.',
    'All active tokens for that provider in this tenant are revoked instantly.',
    'The AI provider will see a 401 on the next call — its UI will prompt to reconnect.',
    'Disconnect is non-destructive: it does NOT touch user accounts, schedules, payroll, or audit history.',
  ],
  troubleshooting: [
    'Connection rejected with "policy_blocked" — your tenant owner has not enabled MCP for your role. Ask the owner to toggle "Allow Manager/Staff MCP access" in Settings → AI Connections.',
    'Connection rejected with "invalid_client" — your provider was paused or blocked by ReStaff operations. Check the MCP Service Status panel; contact support if it persists.',
    'Receiving 429 errors in your AI assistant — you hit the per-role hourly limit. Wait the duration in Retry-After (max 1 hour) and try again.',
    '"Issues detected" badge stays on for a provider — view the SuperAdmin Provider Health card, or contact support@getrestaff.co.',
    'AI assistant claims to be connected but tools return nothing — check the Most Used Tools chips on the Usage panel; if no rows appear, the provider session is not actually authenticated. Reconnect from the provider UI.',
  ],
};


/* ─────────────────────────────────────────────
   MCP SETUP — ChatGPT & Claude
─────────────────────────────────────────────── */
ARTICLES['mcp'] = {
  title: 'MCP Setup — Connect ChatGPT & Claude',
  description:
    'Securely connect ChatGPT and Claude to your ReStaff workspace via the Model Context Protocol — scoped, read-only, and revocable in one click.',
  icon: '🔌',
  category: 'AI Assistant',
  sections: [
    { id: 'overview',         label: 'Overview' },
    { id: 'capabilities',     label: 'What you can do' },
    { id: 'security',         label: 'Security & Privacy' },
    { id: 'chatgpt-setup',    label: 'ChatGPT Setup' },
    { id: 'claude-setup',     label: 'Claude Setup' },
    { id: 'prompts',          label: 'Example Prompts' },
    { id: 'troubleshooting',  label: 'Troubleshooting' },
    { id: 'faq',              label: 'FAQ' },
  ],
  overview:
    'The Model Context Protocol (MCP) is an open standard that lets ChatGPT and Claude securely connect to your ReStaff workspace and answer questions about your team, schedules, hours, leave, and labor cost — using your real data, scoped to your tenant only. Setup takes about 60 seconds and requires no API keys. The MCP server URL is:  https://getrestaff.co/api/mcp',
  benefits: [
    'Ask ChatGPT or Claude any workforce question in plain English',
    'Read-only by design — AI can never modify schedules, payroll, or staff records',
    'OAuth-secured connection — revoke access from Settings in one click',
    'Scoped to your tenant only — AI cannot see data from any other business',
    'Five granular scopes — grant exactly what each AI needs',
    'Free with every ReStaff plan — no per-token charges, no API keys',
  ],
  capabilities: {
    intro: 'Once connected, ChatGPT or Claude can read the following workforce data inside your tenant. Each capability maps to a scope you explicitly approve during connection — you can decline any scope and only grant what you need.',
    groups: [
      {
        scope: 'staff.read',
        title: 'Staff Directory',
        items: [
          'List employees by name, role, location, or status',
          'See active vs. inactive headcount',
          'Find who covers a specific role or location',
          'No PII exposed (passwords, PINs, and government IDs are excluded)',
        ],
      },
      {
        scope: 'schedule.read',
        title: 'Scheduling',
        items: [
          'View current and upcoming shifts',
          'Spot coverage gaps and over-scheduled days',
          'Cross-check assigned roles against shift demand',
          'Summarize weekly publishing status',
        ],
      },
      {
        scope: 'timesheet.read',
        title: 'Timesheets',
        items: [
          'Review clock-in / clock-out hours per employee',
          'Detect approaching-overtime patterns',
          'Compare scheduled vs. worked hours',
          'Surface late, early, and missed-punch events',
        ],
      },
      {
        scope: 'leave.read',
        title: 'Leave',
        items: [
          'Read leave balances for any active employee',
          'Identify upcoming leave windows',
          'Summarize leave trends by month or location',
        ],
      },
      {
        scope: 'analytics.read',
        title: 'Analytics',
        items: [
          'Total labor cost by location, week, or role',
          'Headcount and workforce summaries',
          'High-level coverage / cost ratios',
        ],
      },
    ],
  },
  security: {
    intro: 'MCP is the only way an external AI can read your ReStaff data — and it is locked down end-to-end.',
    rows: [
      { name: 'Read-only',         desc: 'No write scopes are offered. AI cannot create, edit, or delete anything.' },
      { name: 'Tenant isolation',  desc: 'Every MCP request is filtered by your restaurant_group_id at the database layer. AI cannot see data from any other tenant.' },
      { name: 'OAuth 2.1 + PKCE',  desc: 'Each connection completes a standard OAuth flow with proof-key-for-code-exchange — no shared secrets ever leave ReStaff.' },
      { name: 'Granular scopes',   desc: 'You approve each of the five scopes individually. Decline any scope you do not need.' },
      { name: 'One-click revoke',  desc: 'Disconnect any provider from Settings → AI Connections — all active tokens are revoked instantly.' },
      { name: 'Audit log',         desc: 'Every MCP call is recorded by tenant, scope, IP, and tool name. Owners can export the audit CSV.' },
      { name: 'Rate limiting',     desc: 'Per-tenant fair-use limits prevent runaway AI loops.' },
    ],
  },
  steps: [
    /* ───────────── ChatGPT (steps 1–4) ───────────── */
    {
      title: 'ChatGPT · Step 1 — Open Settings → Connectors',
      desc: 'Open ChatGPT in your browser, click your profile icon, then choose Settings. In the left rail, click "Connectors" (some workspaces label it "Apps & Connectors"). This is where ChatGPT lists all MCP servers it can talk to.',
      tip: 'You need ChatGPT Pro / Business / Enterprise with Developer mode enabled. If you do not see "Connectors", upgrade or enable Developer mode under Settings → Advanced.',
      screenshot: 'https://restaff.b-cdn.net/Screenshots/gpt-mpc1.png',
    },
    {
      title: 'ChatGPT · Step 2 — Add a custom MCP connector',
      desc: 'Click "Add custom MCP server" (or "Create connector"). In the Server URL field, paste exactly:  https://getrestaff.co/api/mcp  — ChatGPT will discover the OAuth endpoints automatically. Give the connector a friendly name (e.g. "ReStaff") and submit.',
      tip: 'Always paste the URL exactly — http or trailing slashes will fail discovery.',
      screenshot: 'https://restaff.b-cdn.net/Screenshots/gpt-mpc2.png',
    },
    {
      title: 'ChatGPT · Step 3 — Sign in to ReStaff and approve scopes',
      desc: 'ChatGPT redirects to ReStaff. Sign in with the same email and password you use today. You will see a consent screen listing the five read-only scopes — check each one you want to grant and click "Sign in & Approve".',
      tip: 'Decline any scope you do not need. You can reconnect later with a different scope mix.',
      screenshot: 'https://restaff.b-cdn.net/Screenshots/gpt-mpc3.png',
    },
    {
      title: 'ChatGPT · Step 4 — Connected',
      desc: 'ChatGPT shows a green "Connected" badge for ReStaff. The connector also appears under Settings → AI Connections inside ReStaff with the scopes you granted, the last-used timestamp, and a one-click Disconnect button.',
      tip: 'In a new ChatGPT conversation, tap the + (Tools) icon and toggle ReStaff on. Ask "Show me today\'s schedule" to test.',
      screenshot: 'https://restaff.b-cdn.net/Screenshots/gpt-mpc4.png',
    },

    /* ───────────── Claude (steps 5–8) ───────────── */
    {
      title: 'Claude · Step 1 — Open Connectors',
      desc: 'In claude.ai, click your profile and choose Settings, then "Connectors" in the left rail. Or go directly to https://claude.ai/settings/connectors. Click "Add custom connector".',
      tip: 'Available on Claude Pro, Max, Team, and Enterprise plans.',
      screenshot: 'https://restaff.b-cdn.net/Screenshots/claude-mcp1.png',
    },
    {
      title: 'Claude · Step 2 — Add the ReStaff MCP server',
      desc: 'Paste the server URL:  https://getrestaff.co/api/mcp  — Claude detects the OAuth flow automatically. Name it "ReStaff" and click Add.',
      tip: 'Claude validates the OAuth discovery document before adding — a successful "Add" means the handshake is healthy.',
      screenshot: 'https://restaff.b-cdn.net/Screenshots/claude-mcp2.png',
    },
    {
      title: 'Claude · Step 3 — Approve the scopes',
      desc: 'Claude opens the ReStaff sign-in page in a popup. Sign in, review the five scopes, and click "Sign in & Approve". You return to Claude automatically.',
      tip: 'You can revoke any time from Settings → AI Connections inside ReStaff.',
      screenshot: 'https://restaff.b-cdn.net/Screenshots/claude-mcp3.png',
    },
    {
      title: 'Claude · Step 4 — Connected',
      desc: 'ReStaff appears in your Claude sidebar with a checkmark. Open any conversation, click the + icon, and toggle ReStaff on. Try: "Summarize coverage gaps this week."',
      tip: 'Each conversation can independently enable or disable the ReStaff connector via the + menu.',
      screenshot: 'https://restaff.b-cdn.net/Screenshots/claude-mcp4.png',
    },
  ],
  promptExamples: [
    {
      group: 'Quick checks',
      prompts: [
        'Show me today\'s schedule.',
        'Who is working at the downtown location right now?',
        'List all active managers.',
      ],
    },
    {
      group: 'Coverage & gaps',
      prompts: [
        'Summarize staffing coverage for this week.',
        'Which days next week are under-scheduled?',
        'Where do I have shifts without an assigned employee?',
      ],
    },
    {
      group: 'Hours & overtime',
      prompts: [
        'Which employees are approaching overtime this week?',
        'Compare scheduled vs. worked hours for the last 7 days.',
        'How many hours did each employee work last pay period?',
      ],
    },
    {
      group: 'Leave',
      prompts: [
        'Show leave trends for the last 90 days.',
        'Who has the most unused leave balance?',
        'List approved leave requests for next month.',
      ],
    },
    {
      group: 'Labor analytics',
      prompts: [
        'Show labor cost by location for the last 30 days.',
        'Which role has the highest labor spend?',
        'Give me a workforce summary for the executive team.',
      ],
    },
  ],
  troubleshooting: [
    'Connector setup fails with "Error fetching OAuth configuration" — confirm you typed the URL exactly: https://getrestaff.co/api/mcp (lowercase, https, no trailing slash).',
    '"This MCP server does not implement OAuth" — your AI client is older than the 2025-06-18 MCP spec, or the connector was added while ReStaff was being deployed. Remove and re-add the connector.',
    'OAuth approval page shows "invalid_user_credentials" — you mistyped your ReStaff password. Use the exact email/password from your normal ReStaff sign-in.',
    'OAuth approval page shows "policy_blocked" or "manager_mcp_disabled_by_owner" — your tenant owner has not enabled MCP access for your role. Owner can toggle this under Settings → AI Connections → "Who can connect AI tools".',
    'Connector shows "Connected" but ChatGPT/Claude returns "No data returned" — make sure you actually granted the relevant scope during sign-in. Disconnect from Settings → AI Connections and reconnect, ticking the missing scope.',
    'AI returns "Permission denied" or "tenant_unresolved" — your user account has no tenant assigned (rare; usually old signups). Contact support@getrestaff.co with your account email.',
    'Disconnected by accident — just reconnect the same way you set it up. The audit log keeps a record of the previous session and a new token is issued.',
    'Token expired notice in your AI tool — MCP tokens last 1 hour by design. Your AI client will refresh silently; if it does not, open a new chat or reconnect the provider.',
    'See "Issues detected" badge on the MCP Service Status card — the platform has paused a provider temporarily. Wait a few minutes and retry; if it persists, contact support.',
  ],
  faq: [
    {
      q: 'Does the AI see my data from other tenants?',
      a: 'No. Every MCP request is scoped to your restaurant_group_id at the database layer. Cross-tenant access is impossible — even a super-admin token resolves to a single tenant when used via MCP.',
    },
    {
      q: 'Can the AI modify my schedule, payroll, or staff records?',
      a: 'No. MCP exposes read-only scopes only. There is no write API surface, period.',
    },
    {
      q: 'Do I need an OpenAI or Anthropic API key?',
      a: 'No. MCP runs inside your existing ChatGPT or Claude subscription — your AI provider invokes ReStaff on your behalf using OAuth. No additional billing or keys required from your side.',
    },
    {
      q: 'How do I revoke access?',
      a: 'Open Settings → AI Connections, find the provider row, click Disconnect. All active tokens for that provider are revoked immediately; the next AI request will be rejected.',
    },
    {
      q: 'Can I see what the AI is accessing?',
      a: 'Yes. The MCP Usage panel in Settings shows daily and 30-day request counts, the most-used tools, and the per-provider scopes. Owners can also export a CSV audit log.',
    },
    {
      q: 'What happens if I hit the rate limit?',
      a: 'You get a clear 429 response with a Retry-After header (max 1 hour). The AI will pause and retry automatically. The limit exists to protect your tenant from runaway agent loops.',
    },
    {
      q: 'Why is Gemini not listed as an option right now?',
      a: 'Google has not yet shipped a consumer-facing UI for adding custom MCP servers to Gemini in the web app. As soon as they do, the existing ReStaff MCP server URL will work — no changes needed on our side.',
    },
    {
      q: 'I am a manager — why can\'t I connect?',
      a: 'By default only the tenant owner can connect AI assistants. Ask the owner to toggle "Allow Manager MCP access" under Settings → AI Connections.',
    },
  ],
};

